// Paris, "Rain on the attic window". Inside a painter's chambre de bonne under the zinc roofs, looking
// out through a big dormer window. Rain beads, slides, merges and pools on the glass. Outside: a hazy
// moon behind moving clouds, a sea of zinc roofs with terracotta chimney pots, neighbours' little lives
// in the mansard windows across the street, a pigeon pair on a chimney and, small and gold in the
// distance, the Eiffel Tower with its rotating beacon (it sparkles three times a loop). Inside: a tuxedo
// cat asleep on the sill, a desk with a sketchbook and a steaming bowl, a turntable, an easel with a
// half-finished painting of this view, string lights on the beam, hanging pothos, dust and a moth in the lamp light.
// The billboard is a floodlit rooftop sign for an invented night cafe on the roofs right of the tower.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, FONT3, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const VP = { x: 240, y: 118 };                       // eye level / vanishing point of the room
const GL = { x0: 148, x1: 332, y0: 42, y1: 190 };    // glass area (x1, y1 exclusive)
const COLS = [[148, 237], [242, 332]];               // two casements, meeting stiles at 237..242
const ROWS = [[42, 86], [89, 139], [142, 190]];      // three rows of panes, 3 px muntins between
const PANES = [];
for (const [y0, y1] of ROWS) for (const [x0, x1] of COLS) PANES.push({ x0, x1, y0, y1, w: x1 - x0, h: y1 - y0 });
const FR = { x0: 141, x1: 339, y0: 35, y1: 196 };    // painted window frame (outer edge)
const AL = { x0: 125, x1: 355, y0: 24, y1: 207 };    // opening in the mansard wall (deep embrasure)
const gableY = x => 86 - 0.54 * Math.min(x, W - x);  // where the sloped ceiling meets the wall
const BEAM = { y0: 0, y1: 9 };                       // tie beam across the room
const DESK = { top: 214, front: 224, bottom: 233, bx0: 120, bx1: 360, fx0: 106, fx1: 374 };
const FLOOR = 240;                                   // wall / floor line
const TOWER = { x: 214, base: 138, h: 47 };          // far Eiffel Tower: antenna tip at row 91, feet hidden by the roofs
const MOON = { x: 298, y: 62 };
const SPIRE = { x: 304, top: 108 };                  // distant church (the bell)
const LAMP = { x: 158, y: 182 };                     // desk-lamp bulb
const MOTH = { x: 155, y: 172, cx: 166, cy: 184 };   // a moth: its spot on the lamp shade, the centre of its flight
const CAT = { x: 236, y: 181 };                      // cat sprite origin (on the sill cushion)
const BOWL = { x: 300, y: 205 };                     // cafe au lait bowl (rim centre)
const TT = { cx: 36, cy: 190, rx: 16, ry: 5.5 };     // record on the turntable
const EASEL = { x0: 384, y0: 118, w: 58, h: 58 };    // canvas on the easel
const WINS = [                                       // neighbours' mansard windows across the street
  { x: 173, y: 165, w: 10, h: 13, kind: 'water' },
  { x: 205, y: 165, w: 10, h: 13, kind: 'read' },
  { x: 291, y: 169, w: 10, h: 13, kind: 'dance' },
  { x: 315, y: 169, w: 10, h: 13, kind: 'tv' },
];
const PIGEONS = { x: 211, y: 151 };                  // on the chimney stack of the left building
// the billboard: a lamp-lit sign on the roof across the street, right of the tower. Its face sits inside the middle
// right-hand pane (x 242..332, y 89..139), so no mullion crosses it; its lattice goes down behind the muntin to that
// roof's ridge (row 163), where a dormer is 10 px wide: at that scale the face is a 4.4 x 1.8 m panel, not a sign as
// wide as the far roofs' houses (issue #73).
const BB = { x: 245, y: 117, w: 44, h: 18 };         // the face (the app lays its clickable spot exactly here)
const BB_LAMPS = [252, 267, 282];                    // three gooseneck floodlamps hanging over the top edge
const BB_LENS = BB.y - 4;                            // their lens row
// three hidden things (site/js/ui/secrets.js, exported as `secrets`): a mouse at the end of the upper shelf, a heart
// someone drew on the misted pane, and a red balloon that comes up behind the roofs on its string for a while
const MOUSE = { x: 92, y: 105 };                     // sprite origin: it sits on the shelf (row 110), beside the succulent
const HEART = { x: 305, y: 177 };                    // drawn in the mist between the two dormers on the right
const BALLOON = { x: 191, y: 147, rise: 14 };        // its top-left once it is up; it comes up `rise` rows from behind the roof across the street

/* ================= Timed events (loop seconds) ================= */
const SPARKLE = [30, 110, 190];                      // Eiffel Tower sparkles for 12 s (chime at the start)
const SPARK_DUR = 12;
const BELL = [72, 75, 78];                           // distant church bell, three strikes
const STRETCH = 150;                                 // the cat stretches once (6 s)
const EAR_TW = [17.2, 58.4, 93.1, 131.6, 176.8, 212.3];
const TAIL_FL = [40.5, 121.3, 204.6];
const PLANE = { t0: 158, t1: 222 };                  // a jet crosses high up, right to left
const READ_OFF = [168, 214];                         // the reader switches the lamp off, then on again
const DANCE_ON = [48, 236];                          // the couple's window is lit (dark across the wrap)
const WATERING = [[18, 64], [138, 176]];             // someone waters the plants
const PAGE_TURN = [26, 58, 91, 124, 156, 222];
const BULB_FLICK = [[96, 98.4], [201.2, 202.1]];     // one loose bulb on the string
const BB_FLICK = [[57.5, 59.6], [133.2, 134.3], [183.6, 185.4]];   // the billboard's right-hand floodlamp stutters
const MOTH_FLY = [[14, 66], [101, 139], [172, 226]];  // the moth circles the bulb, resting on the shade in between
const BALLOON_UP = [[84, 102], [216, 234]];          // the red balloon shows: 3 s coming up, 12 s on its string, 3 s going down

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = C(a), B = C(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    clear(i, j) { if (i >= 0 && j >= 0 && i < w && j < h) d[(j * w + i) * 4 + 3] = 0; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2]]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** like pick() but the dithered band between two tones is narrower (cleaner flat surfaces) */
function pickS(ramp, v, i, j, soft = 0.4) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
function inPoly(px, py, pts) {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) c = !c;
  }
  return c;
}
/** call fn(x, y) for every pixel whose centre lies inside the polygon */
function fillPoly(pts, fn) {
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  for (let y = Math.floor(Math.min(...ys)); y <= Math.ceil(Math.max(...ys)); y++)
    for (let x = Math.floor(Math.min(...xs)); x <= Math.ceil(Math.max(...xs)); x++)
      if (inPoly(x + 0.5, y + 0.5, pts)) fn(x, y);
}
/** thick pixel line: fn(x, y, s) with s the 0..1 position along the line */
function thickLine(x0, y0, x1, y1, w0, w1, fn) {
  const L = Math.hypot(x1 - x0, y1 - y0), nx = -(y1 - y0) / L, ny = (x1 - x0) / L;
  const pts = [[x0 + nx * w0 / 2, y0 + ny * w0 / 2], [x1 + nx * w1 / 2, y1 + ny * w1 / 2],
    [x1 - nx * w1 / 2, y1 - ny * w1 / 2], [x0 - nx * w0 / 2, y0 - ny * w0 / 2]];
  fillPoly(pts, (x, y) => {
    const s = clamp(((x + 0.5 - x0) * (x1 - x0) + (y + 0.5 - y0) * (y1 - y0)) / (L * L), 0, 1);
    const across = ((x + 0.5 - x0) * nx + (y + 0.5 - y0) * ny) / (lerp(w0, w1, s) / 2);   // -1..1
    fn(x, y, s, across);
  });
}
function line(p, x0, y0, x1, y1, col) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), col);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(i, y, col); } });
  return p.done();
}
/** dithered soft disc used for smoke, steam and near dust */
function makePuff(r, col, maxA) {
  const s = r * 2 + 1, p = pix(s, s), c = C(col);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.2) * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(Math.min(1, q) * maxA * 255));
  }
  return p.done();
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}

/* ================= Palettes ================= */
const SKY = ['#05081a', '#070b20', '#090e27', '#0b122e', '#0e1636', '#111a3e', '#141f47', '#182550', '#1c2a58',
  '#213160', '#263868', '#2d3f70', '#344777', '#3d507e', '#475984', '#526389'];
const PLASTER = ['#1c110c', '#26170f', '#321e13', '#3f2617', '#4d2f1b', '#5d3a1f', '#6e4524', '#81522a', '#955f30',
  '#a96e37', '#bc7f40', '#cc924c'];
const COOLP = ['#17161f', '#1f1e2b', '#282838', '#323347', '#3e4157', '#4b4f67'];   // plaster in window light
const WOOD = ['#140b07', '#1f120b', '#2d1a0f', '#3d2414', '#4f2f19', '#633b1f', '#7a4a27', '#935a2f', '#ab6b38'];
const HONEY = ['#24140b', '#361f10', '#4b2b15', '#63391b', '#7d4a22', '#98602b', '#b27634', '#c98d42', '#dca653'];
const CREAM = ['#3a2e26', '#56473a', '#76644f', '#978366', '#b6a07e', '#cdb893', '#e0cda6', '#efdfbb', '#fbefd2'];
const FLOORP = ['#140c08', '#1c110b', '#25160e', '#2f1c11', '#3a2315', '#462b19', '#53331d'];
const ZINC = ['#10152a', '#161c33', '#1c233d', '#232b47', '#2b3452', '#353f5e', '#43506e', '#56637f', '#6d7a93'];
const HAZE = ['#161d3a', '#1a2242', '#1f2849', '#252f51', '#2c3659'];
const TERRA = ['#3e1a12', '#5e2818', '#80361f', '#a44828', '#c25e35', '#d97a4a'];
const STACK = ['#221f2c', '#2c2837', '#373243', '#433d50', '#524b5e', '#635b6e'];
const GOLD = ['#3a1e06', '#643608', '#96560c', '#c67c16', '#e8a226', '#fcc84a', '#ffe590', '#fff6d0'];
const LEAF = ['#0b1a10', '#12291a', '#1b3d22', '#265428', '#356c30', '#4b863a', '#66a044', '#8cba58'];
const MUSTARD = ['#3a220c', '#5a3612', '#7e4e18', '#a2681f', '#c28629', '#dca43a'];

/* ================= Outside, seen through the glass (canvases cover x 140..340, y 36..196) ================= */
const OX = 140, OY = 36, OW = 200, OH = 160;
const STONE = ['#252735', '#323545', '#404456', '#51566a', '#656b80', '#7c8398'];

function renderSky() {
  const p = pix(OW, OH);
  for (let j = 0; j < OH; j++) for (let i = 0; i < OW; i++) {
    const x = i + OX, y = j + OY;
    let v = Math.pow(clamp((y - 38) / 108, 0, 1), 1.15) * 0.8;
    v += Math.max(0, 1 - Math.hypot((x - MOON.x) / 86, (y - MOON.y) / 62)) * 0.2;     // moonlit haze
    v += Math.max(0, 1 - Math.hypot((x - 226) / 150, (y - 150) / 44)) * 0.13;        // city glow low down
    p.set(i, j, pick(SKY, v, x, y));
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(31);
  while (STARS.length < 9) {
    const x = GL.x0 + 4 + Math.floor(r() * 176), y = GL.y0 + 3 + Math.floor(r() * 40);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 30) continue;
    STARS.push({ x, y, k: 13 + Math.floor(r() * 30), ph: r(), b: 0.25 + r() * 0.35 });
  }
}
/** waxing gibbous moon, pale and soft */
function renderMoon() {
  const R = 6.2, s = 15, c0 = 7, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - c0, dy = y - c0, d = Math.hypot(dx, dy);
    if (d > R + 0.3) continue;
    if (Math.hypot(dx + 11.4, dy + 1) < R + 3.4) continue;                            // thin unlit sliver on the left
    let col = '#f1ebd3';
    if (d > R - 0.9) col = dx > 0 ? '#e2d9ba' : '#d6ccac';
    if ((dx - 1) ** 2 + (dy + 2) ** 2 < 2.2 || (dx + 2) ** 2 + (dy - 2) ** 2 < 1.4 || (dx - 2.5) ** 2 + (dy - 2.5) ** 2 < 0.8) col = '#d9cfb0';
    p.set(i, y, col);
  }
  return p.done();
}

/* ================= Clouds: tileable, soft-edged, moonlit tops, city-lit bellies ================= */
const CW = 256;
function makeClouds(seed, h, pal, thr, sx, sy) {
  const D = new Float32Array(CW * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < CW; x++) {
    const n = 0.55 * vnoise(x / (32 * sx), y / (11 * sy), seed, 8 / sx) + 0.3 * vnoise(x / (16 * sx), y / (5.5 * sy), seed + 1, 16 / sx)
      + 0.15 * vnoise(x / (8 * sx), y / (2.8 * sy), seed + 2, 32 / sx);
    const band = 1 - Math.abs(y - h * 0.5) / (h * 0.5);
    D[y * CW + x] = n * (0.45 + 0.7 * band);
  }
  const get = (x, y) => (y < 0 || y >= h) ? 0 : D[y * CW + ((x % CW) + CW) % CW];
  const p = pix(CW, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < CW; x++) {
    const v = D[y * CW + x], th = thr + (bayer(x, y) - 0.5) * 0.05;
    if (v <= th - 0.05) continue;
    if (v <= th) { if (bayer(x + 1, y) < 0.5) p.set(x, y, pal.body, 150); continue; }   // dithered soft edge
    let col = pal.body;
    if (get(x, y - 1) <= th) col = pal.top;
    else if (get(x, y + 1) <= th) col = pal.bot;
    else if (v > th + 0.13) col = pal.core;
    p.set(x, y, col);
  }
  return p.done();
}

/* ================= The Eiffel Tower, small and gold (sprite origin: world row TW.top, the antenna tip) ================= */
// The real profile at 7 m a row: curved legs splayed over the great arch, the 1st floor (57 m), the lattice section
// narrowing to the 2nd floor (116 m), the long third section (3 px under the top, 5 over the 2nd floor, a touch stout
// so it reads as a shaft and not a needle), the top platform (276 m), the lantern with the beacon, the antenna (330 m)
const TW = { w: 25, cx: 12, top: TOWER.base - TOWER.h, mpx: 330 / TOWER.h };
const twRow = hm => Math.round(TOWER.h - hm / TW.mpx);              // sprite row of a height in metres
const TW_FLOORS = [twRow(57.6), twRow(115.7), twRow(276)];          // sprite rows of the 1st, 2nd and top floors
const TW_BEACON = twRow(300);                                        // the lantern's top, where the beacon turns
/** half-width (px) of the tower's outline at height hm (m) */
function towerHalf(hm) {
  if (hm < 57.6) return 62.5 * Math.exp(-hm / 90) / TW.mpx;           // legs, 125 m apart at the foot
  if (hm < 115.7) return 33 * Math.exp(-(hm - 57.6) / 121) / TW.mpx;   // 1st floor to 2nd
  return 1.45 + 1.45 * Math.pow((276 - hm) / 160.3, 1.6);              // third section
}
function renderTower() {
  const hgt = TOWER.h + 1, p = pix(TW.w, hgt), mask = [], [r1, r2, r3] = TW_FLOORS;
  const solid = new Uint8Array(TW.w * hgt), lvl = new Float32Array(TW.w * hgt);
  for (let yl = r3 + 1; yl < hgt; yl++) {
    const hm = (TOWER.h - yl) * TW.mpx, hw = towerHalf(hm);
    const arch = hm < 44 ? Math.min(hw - 3, 6 * Math.sqrt(1 - (hm / 44) ** 2)) : 0;   // the great arch; legs 3 px wide
    const gap = hm > 60 && hm < 100 ? 2 * (1 - (hm - 60) / 40) : 0;                   // the legs still apart over the 1st floor
    for (let i = 0; i < TW.w; i++) {
      const dx = Math.abs(i - TW.cx), cov = clamp(hw + 0.5 - dx, 0, 1);
      if (cov < 0.3 || dx < arch - 0.25 || dx < gap - 0.25) continue;
      solid[yl * TW.w + i] = 1;
      lvl[yl * TW.w + i] = (cov < 0.75 ? 0.2 + 0.3 * cov : 0.6) + 0.12 * yl / hgt;     // part-covered edges fade
    }
  }
  const S = (i, y) => i >= 0 && y >= 0 && i < TW.w && y < hgt && solid[y * TW.w + i];
  for (let yl = r3 + 1; yl < hgt; yl++) {
    let x0 = TW.w, x1 = -1;
    for (let i = 0; i < TW.w; i++) if (S(i, yl)) { x0 = Math.min(x0, i); x1 = Math.max(x1, i); }
    for (let i = 0; i < TW.w; i++) {
      if (!S(i, yl)) continue;
      let v = lvl[yl * TW.w + i];
      const edge = !S(i - 1, yl) || !S(i + 1, yl);
      if (edge) v += 0.14;
      else if (x1 - x0 >= 4 && ((i + yl) % 3 === 0 || (i - yl + 30) % 3 === 0)) v -= 0.24;   // lattice
      if (i > TW.cx && !edge) v -= 0.06;
      p.set(i, yl, pick(GOLD, v, i, yl));
    }
  }
  // floors: brighter bands, the 1st two rows deep and 1 px proud of the legs, the 2nd 1 px proud of the shaft above
  const h1 = Math.round(towerHalf(50)) + 1, h2 = Math.round(towerHalf(110));
  for (let i = -h1; i <= h1; i++) { p.set(TW.cx + i, r1, GOLD[6]); p.set(TW.cx + i, r1 + 1, GOLD[4]); }
  for (let i = -h2; i <= h2; i++) p.set(TW.cx + i, r2, GOLD[6]);
  for (let i = -2; i <= 2; i++) p.set(TW.cx + i, r3, GOLD[Math.abs(i) === 2 ? 3 : 6]);
  p.set(TW.cx - 1, r3 - 1, GOLD[4]); p.set(TW.cx + 1, r3 - 1, GOLD[3]);          // lantern: a stepped foot, a 1 px shaft
  for (let yl = TW_BEACON; yl < r3; yl++) p.set(TW.cx, yl, GOLD[yl === TW_BEACON ? 7 : 6]);
  for (let yl = 0; yl < TW_BEACON; yl++) p.set(TW.cx, yl, yl === 0 ? '#d8d4dc' : '#a8a0a8');   // antenna
  for (let yl = TW_BEACON; yl < hgt; yl++) for (let i = 0; i < TW.w; i++) if (p.alpha(i, yl)) mask.push([i, yl]);
  return { c: p.done(), mask };
}

/* ================= Rooftops: far haze, the zinc sea, the buildings across the street ================= */
/** one mansard building block into painter p (world coords); returns chimney tops for smoke */
function mansard(p, b, pal, seamCol, ridgeCol, dormerCol) {
  for (let x = b.x0; x < b.x1; x++) {
    const i = x - OX;
    for (let y = b.top; y < OY + OH; y++) {
      const j = y - OY;
      let v = 0.55 - (y - b.top) * 0.014 + (b.lit || 0) + (y < b.top + 3 ? 0.25 : 0);
      let col = pick(pal, v, x, y);
      if ((x - b.x0) % b.seam === 0 && y > b.top + 3) col = seamCol;
      if (y === b.top) col = ridgeCol;
      p.set(i, j, col);
    }
  }
  for (const d of b.dormers || []) {                                  // tiny dormers: pale frame, dark or lit pane
    p.fill(d[0] - OX - 1, d[1] - OY - 1, 4, 5, dormerCol);
    p.set(d[0] - OX, d[1] - OY - 2, dormerCol); p.set(d[0] - OX + 1, d[1] - OY - 2, dormerCol);
    p.fill(d[0] - OX, d[1] - OY, 2, 3, d[2] ? '#b88a52' : '#0e1224');
  }
}
function chimney(p, x, top, w, h, pots, pal, potW = 1, potH = 3) {
  for (let y = top; y < top + h; y++) for (let k = x; k < x + w; k++) {
    let col = pal[2];
    if (k === x) col = pal[1]; else if (k === x + w - 1) col = pal[3];
    if (y === top) col = pal[4];
    p.set(k - OX, y - OY, col);
  }
  for (let k = x - 1; k <= x + w; k++) p.set(k - OX, top - OY, pal[4]);   // cornice
  const out = [];
  for (const o of pots) {
    const px = x + o;
    for (let y = top - potH; y < top; y++) for (let k = 0; k < potW; k++) {
      let col = k === 0 ? TERRA[3] : TERRA[2];
      if (potW === 1) col = y === top - potH ? TERRA[1] : TERRA[3];
      else if (y === top - potH) col = k === potW - 1 ? TERRA[3] : TERRA[5];
      else if (y === top - potH + 1 && potW > 1) col = k === 0 ? TERRA[4] : TERRA[1];
      p.set(px + k - OX, y - OY, col);
    }
    out.push([px + (potW >> 1), top - potH]);
  }
  return out;
}
const potsFor = (w, n) => Array.from({ length: n }, (_, i) => 1 + Math.round(i * (w - 3) / Math.max(1, n - 1)));
function renderFar() {
  const p = pix(OW, OH), r = rng(77), tiny = [];
  let x = OX - 4;
  while (x < OX + OW + 4) {
    const w = 6 + Math.floor(r() * 12);
    let top = 126 + Math.floor(r() * 10);
    if (x + w > TOWER.x - 14 && x < TOWER.x + 14) top = Math.max(top, TOWER.base - 1);   // the roofs dip: only the tower's feet hide
    for (let k = x; k < x + w; k++) for (let y = top; y < OY + OH; y++) {
      const v = 0.3 + (y - top) * 0.02 + (y === top ? 0.35 : 0);
      p.set(k - OX, y - OY, pick(HAZE, v, k, y));
    }
    if (r() < 0.55) {                                                 // a chimney stack with pots
      const cx = x + 1 + Math.floor(r() * (w - 3)), ch = 2 + Math.floor(r() * 3);
      for (let y = top - ch; y < top; y++) { p.set(cx - OX, y - OY, HAZE[2]); p.set(cx + 1 - OX, y - OY, HAZE[3]); }
      p.set(cx - OX, top - ch - 1, '#3a2a36');
    }
    for (let n = 0; n < 2; n++) if (r() < 0.6) tiny.push([x + 1 + Math.floor(r() * (w - 2)), top + 2 + Math.floor(r() * 6), r()]);
    x += w;
  }
  // distant church: nave, bell tower and a slim spire (the bell rings at 72 s)
  const sx = SPIRE.x;
  for (let k = sx - 9; k <= sx + 9; k++) for (let y = 124; y < 140; y++) p.set(k - OX, y - OY, pick(HAZE, 0.45 + (y === 124 ? 0.3 : 0), k, y));
  for (let k = sx - 3; k <= sx + 3; k++) for (let y = 115; y < 124; y++) p.set(k - OX, y - OY, k === sx - 3 ? HAZE[4] : HAZE[2]);
  for (let y = SPIRE.top; y < 115; y++) {
    const hw = Math.floor((y - SPIRE.top) / 3.4);
    for (let k = sx - hw; k <= sx + hw; k++) p.set(k - OX, y - OY, k === sx - hw ? HAZE[4] : HAZE[2]);
  }
  p.set(sx - OX, SPIRE.top - 1 - OY, HAZE[4]); p.set(sx - OX, SPIRE.top - 2 - OY, HAZE[4]); p.set(sx - 1 - OX, SPIRE.top - 2 - OY, HAZE[3]); p.set(sx + 1 - OX, SPIRE.top - 2 - OY, HAZE[3]);
  p.set(sx - OX, 118 - OY, '#8a7658'); p.set(sx - OX, 119 - OY, '#6a5a48');          // belfry openings, faintly lit
  return { c: p.done(), tiny };
}
function renderMid() {
  const p = pix(OW, OH), r = rng(91), smoke = [], lit = [];
  const rows = [
    { top: [135, 143], seed: 0, pal: ZINC.slice(1, 6), seam: ZINC[4], ridge: ZINC[7] },
    { top: [144, 152], seed: 1, pal: ZINC.slice(0, 5), seam: ZINC[3], ridge: ZINC[6] },
  ];
  for (const row of rows) {
    let x = OX - 6;
    while (x < OX + OW + 6) {
      const w = 12 + Math.floor(r() * 22);
      let top = row.top[0] + Math.floor(r() * (row.top[1] - row.top[0]));
      if (row.seed === 0 && x + w > TOWER.x - 12 && x < TOWER.x + 12) top = Math.max(top, 139);
      const dormers = [];
      if (r() < 0.55) for (let dx = 4 + Math.floor(r() * 4); dx < w - 4; dx += 9 + Math.floor(r() * 6)) {
        const on = r() < 0.4;
        dormers.push([x + dx, top + 4 + Math.floor(r() * 2), on]);
        if (on) lit.push([x + dx, top + 4]);
      }
      mansard(p, { x0: x, x1: x + w, top, seam: 3, dormers, lit: row.seed ? -0.05 : 0.05 }, row.pal, row.seam, row.ridge, STONE[row.seed ? 2 : 3]);
      if (r() < 0.8) {
        const cw = 3 + Math.floor(r() * 4), cx = x + 1 + Math.floor(r() * Math.max(1, w - cw - 2));
        const ch = 3 + Math.floor(r() * 4);
        const pots = chimney(p, cx, top - ch, cw, ch, potsFor(cw, 2 + Math.floor(r() * 3)), row.seed ? STACK.slice(0, 5) : STACK.slice(1, 6));
        if (r() < 0.35) smoke.push(pots[Math.floor(r() * pots.length)]);
      }
      x += w;
    }
  }
  return { c: p.done(), smoke, lit };
}
/** the two buildings across the street: big zinc mansards with dormers (neighbours) and chimney stacks */
function renderNear() {
  const p = pix(OW, OH);
  const blocks = [{ x0: OX, x1: 240, top: 160 }, { x0: 240, x1: OX + OW, top: 163 }];
  for (const b of blocks) {
    for (let x = b.x0; x < b.x1; x++) for (let y = b.top; y < OY + OH; y++) {
      let v = 0.5 - (y - b.top) * 0.012;
      let col = pick(ZINC.slice(0, 6), v, x, y);
      if ((x - b.x0) % 4 === 1 && y > b.top + 2) col = y % 7 === 0 ? ZINC[2] : ZINC[4];   // standing seams
      if (y === b.top) col = ZINC[7]; else if (y === b.top + 1) col = ZINC[5]; else if (y === b.top + 2) col = ZINC[1];
      p.set(x - OX, y - OY, col);
    }
  }
  for (let y = 160; y < OY + OH; y++) { p.set(240 - OX, y - OY, STONE[1]); p.set(241 - OX, y - OY, STONE[2]); }   // party wall
  for (const w of WINS) {                                             // dormers: stone surround, little pediment
    const x0 = w.x - 3, x1 = w.x + w.w + 3, y0 = w.y - 4, y1 = w.y + w.h + 2;
    for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) {
      let col = STONE[3];
      if (x === x0) col = STONE[4]; else if (x === x1 - 1) col = STONE[1];
      if (x > x0 && x < x1 - 1 && (x === w.x - 1 || x === w.x + w.w) && y >= w.y - 1) col = STONE[0];
      p.set(x - OX, y - OY, col);
    }
    for (let k = 0; k < 4; k++) for (let x = x0 + k; x < x1 - k; x++) p.set(x - OX, y0 - 1 - k - OY, k === 3 ? STONE[5] : STONE[k === 0 ? 3 : 4]);
    for (let x = x0 - 1; x <= x1; x++) p.set(x - OX, y1 - OY, STONE[5]);                  // sill
    for (let x = w.x - 1; x <= w.x + w.w; x++) p.set(x - OX, w.y - 1 - OY, STONE[0]);
  }
  const sm = [];
  sm.push(...chimney(p, 216, 151, 17, 10, [12, 15], STACK, 2, 4));                          // left stack (pigeons on the left end)
  sm.push(...chimney(p, 250, 153, 12, 11, [1, 5, 9], STACK, 2, 4));
  // a TV aerial on the right roof
  for (let y = 149; y < 163; y++) p.set(281 - OX, y - OY, '#2a2c3c');
  for (let k = 276; k <= 286; k++) p.set(k - OX, 151 - OY, '#2a2c3c');
  for (let k = 278; k <= 284; k++) p.set(k - OX, 154 - OY, '#2a2c3c');
  return { c: p.done(), smoke: sm };
}

/* ================= The billboard: a floodlit rooftop sign (steel frame, lamps, catwalk, lattice) ================= */
// Its own ad is a painted poster for an invented night cafe, "CAFE LUNE": a steaming cup, gold lettering and a
// chase of bulbs along the top and bottom edges, lit from above by the three lamps.
const STEEL = ['#0a0c18', '#10131f', '#171b2a', '#20253a', '#2c3248', '#3e4458'];
const BORDEAUX = ['#1c0a13', '#2a0e1b', '#3c1424', '#521b2d', '#6a2334', '#822c3a', '#9a3842'];
const PORC = ['#4e403e', '#7a6a5e', '#a8967e', '#cfbd9e', '#ecdcbc', '#fff4dc'];
const COFFEE = ['#1e0c06', '#34180c', '#4e2614'];
const BB_FOOT = 163;                                 // the ridge of the roof across the street (renderNear's right block), where it stands
const BBS = { x: 238, y: BB.y - 9, w: 60, h: BB_FOOT - BB.y + 10 };   // canvas holding the structure
function renderBillboardFrame() {
  const p = pix(BBS.w, BBS.h), ox = BBS.x, oy = BBS.y, S = (x, y, col) => p.set(x - ox, y - oy, col);
  const x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h;      // frame, 1 px around the face
  const lit = x => Math.max(...BB_LAMPS.map(lx => Math.exp(-((x - lx) ** 2) / 30)));
  // lattice: three 2 px legs, braced in two tiers, from under the catwalk down behind the muntin to the near roof's ridge
  const legs = [x0 + 3, ((x0 + x1) >> 1) - 1, x1 - 4], yb = y1 + 3, yf = BB_FOOT, ym = (yb + yf) >> 1;
  for (let k = 0; k < 2; k++) for (const [a, b] of [[yb, ym], [ym, yf]]) {    // dark against the zinc below the muntin
    line(p, legs[k] + 1 - ox, a - oy, legs[k + 1] - ox, b - oy, a < 141 ? STEEL[3] : STEEL[1]);
    line(p, legs[k] + 1 - ox, b - oy, legs[k + 1] - ox, a - oy, a < 141 ? STEEL[3] : STEEL[1]);
  }
  for (let x = legs[0]; x <= legs[2] + 1; x++) S(x, ym, STEEL[1]);            // the girt between the tiers
  for (let x = legs[0]; x <= legs[2] + 1; x++) S(x, yb, x % 3 ? STEEL[4] : '#3a3444');   // stringer under the deck
  for (const lx of legs) for (let y = yb; y <= yf; y++) { S(lx, y, STEEL[1]); S(lx + 1, y, y < yb + 2 ? '#3a3444' : STEEL[4]); }
  for (let y = yb; y <= yf; y++) { S(x1 + 1, y, STEEL[3]); S(x1 + 3, y, STEEL[2]); if (y % 2 === 0) S(x1 + 2, y, STEEL[4]); }   // ladder
  // catwalk: a grating deck 2 px wider than the frame, lit by the face above it
  for (let x = x0 - 1; x <= x1 + 3; x++) {
    S(x, y1 + 1, pickS(['#2a2a3a', '#3c3644', '#524652'], 0.55 + 0.45 * lit(x) - (x < x0 || x > x1 ? 0.3 : 0), x, y1 + 1));
    S(x, y1 + 2, x % 2 ? STEEL[1] : STEEL[3]);
  }
  for (const x of [x0 - 1, x1 + 3]) for (let y = y1 - 2; y <= y1; y++) S(x, y, STEEL[4]);   // guard-rail posts
  // the frame: the top edge catches the lamps
  for (let x = x0; x <= x1; x++) {
    S(x, y0, pickS(['#26243a', '#453a46', '#735c4e', '#a4825c'], 0.1 + 0.6 * lit(x), x, y0));
    S(x, y1, STEEL[4]);
  }
  for (let y = y0 + 1; y < y1; y++) { S(x0, y, STEEL[3]); S(x1, y, STEEL[2]); }
  // gooseneck lamps: arm up from the frame, hood, lens row (the lens is lit per frame)
  for (const lx of BB_LAMPS) {
    S(lx, y0 - 1, STEEL[4]); S(lx, y0 - 2, STEEL[5]);
    S(lx - 1, BB_LENS - 1, STEEL[4]); S(lx, BB_LENS - 1, STEEL[5]); S(lx + 1, BB_LENS - 1, STEEL[3]);
    S(lx - 2, BB_LENS, STEEL[3]); S(lx + 2, BB_LENS, STEEL[2]);
  }
  return p.done();
}
const CUP = [                                        // the poster's cafe cup (local face coords from x 3, y 8)
  '..rrrrrrr....',
  '.rkkkkkkkr...',
  '.bbbbbbbbbhh.',
  '..bbbbbbb..h.',
  '..bbbbbbbhh..',
  '...bbbbb.....',
  'sssssssssss..',
  '.ddddddddd...',
];
const BB_BULBS = Array.from({ length: 14 }, (_, k) => 2 + k * 3);   // chase bulbs along the top and bottom rows
/** the own ad, painted and lit by the lamps (on[k] = lamp k is lit) */
function renderBillboardFace(on) {
  const { w, h } = BB, M = new Uint8Array(w * h), O = new Float32Array(w * h), p = pix(w, h);
  const put = (i, j, m, o = 0) => { if (i >= 0 && j >= 0 && i < w && j < h) { M[j * w + i] = m; O[j * w + i] = o; } };
  const stamp = (s, x, y, m) => { let cx = x; for (const ch of s) { FONT3[ch].forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') put(cx + i, y + j, m); }); cx += 5; } };
  const word = (s, x, y, m) => { stamp(s, x + 1, y + 1, 8); stamp(s, x, y, m); };   // letters with a painted drop shadow
  word('CAFE', 20, 3, 1); word('LUNE', 20, 10, 2);
  const key = { r: 3, k: 5, b: 3, h: 3, s: 6, d: 7 };
  CUP.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) put(3 + i, 8 + j, key[row[i]], row[i] === 'r' ? 0.2 : row[i] === 'b' ? 0.1 - 0.07 * Math.max(0, i - 6) - 0.04 * j : 0); });
  for (const x of BB_BULBS) { put(x, 1, 9); put(x, h - 2, 9); }
  const light = (i, j) => {
    let v = 0.16 + 0.12 * (1 - j / h);
    BB_LAMPS.forEach((lx, k) => {
      if (!on[k]) return;
      const dx = BB.x + i - lx;
      v += 0.34 * Math.exp(-(dx * dx) / 50 - j / 10) + 0.08 * Math.exp(-Math.abs(dx) / 14 - j / 30);   // small lamps: the tower keeps the brightest light
    });
    return clamp(v, 0, 1);
  };
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const L = light(i, j), m = M[j * w + i], o = O[j * w + i];
    let col;
    if (m === 1) col = pickS(CREAM, 0.42 + 0.6 * L, i, j);
    else if (m === 2) col = pickS(GOLD, 0.3 + 0.55 * L, i, j);
    else if (m === 3) col = pickS(PORC, 0.28 + 0.72 * L + o, i, j);
    else if (m === 5) col = pickS(COFFEE, 0.3 + 0.7 * L, i, j);
    else if (m === 6) col = pickS(PORC, 0.2 + 0.6 * L, i, j);
    else if (m === 7) col = pickS(PORC, 0.05 + 0.25 * L, i, j);
    else if (m === 8) col = pickS(BORDEAUX, L * 0.9 - 0.3, i, j);
    else if (m === 9) col = pickS(['#3a1c16', '#5a3420', '#7a4c28'], L, i, j);
    else col = pickS(BORDEAUX, L, i, j, 0.6);
    p.set(i, j, col);
  }
  return gradeAd(p.done(), { tint: '#1a1420', mix: 0.12, wear: 0.5, seed: 5 });   // a pasted poster, a few winters old, in the night's own tint
}

/* ================= The attic room (static; the glass is left transparent) ================= */
const inPane = (x, y) => PANES.some(q => x >= q.x0 && x < q.x1 && y >= q.y0 && y < q.y1);
const lampTerm = (x, y, r = 100) => Math.exp(-Math.hypot((x - LAMP.x) * 0.8, (y - LAMP.y) * 1.05) / r);
function wallV(x, y) {
  let v = 0.2 + 0.36 * lampTerm(x, y) + 0.28 * lampTerm(x, y, 40) + 0.13 * Math.exp(-Math.max(0, y - 12) / 30);
  v -= 0.12 * smooth(0.5, 1.05, Math.abs(x - 240) / 240);
  v -= 0.1 * smooth(160, 240, y);
  v += (vnoise(x / 5, y / 3, 3) - 0.5) * 0.05 + (vnoise(x / 23, y / 13, 5) - 0.5) * 0.05;
  return v;
}
/** warm plaster, dithered towards the cool window-light ramp where `cool` > 0 */
const TWI = PLASTER.map((c, i) => mixHex(c, COOLP[Math.min(5, Math.max(0, i - 3))], 0.45));
function plaster(x, y, v, cool) {
  const lv = Math.floor(clamp(cool, 0, 1) * 2.99 + (bayer(x, y) - 0.5) * 0.35);
  if (lv >= 2) return pickS(COOLP, v * 1.05 + 0.1, x, y);
  if (lv === 1) return pickS(TWI, v, x, y);
  return pickS(PLASTER, v, x, y);
}
/** distance (px) from a point on the wall to the embrasure opening */
function alDist(x, y) {
  const dx = Math.max(AL.x0 - x, 0, x - AL.x1), dy = Math.max(AL.y0 - y, 0, y - AL.y1);
  return Math.hypot(dx, dy);
}
function darken(p, x, y, f, tint = [10, 6, 8]) {
  if (x < 0 || y < 0 || x >= p.w || y >= p.h || !p.alpha(x, y)) return;
  const c = p.get(x, y);
  p.set(x, y, [0, 1, 2].map(i => Math.round(lerp(c[i], tint[i], f))));
}
const CEIL_SEAMS = [6, 13, 21, 30, 40, 51, 63, 76, 90];
function paintShell(p) {
  // back wall and the two sloped ceiling planes
  for (let y = 0; y < FLOOR; y++) for (let x = 0; x < W; x++) {
    const g = gableY(x);
    if (y < g - 5) {                                                  // sloped ceiling: varnished boards
      const d = g - y;
      let v = 0.2 + 0.26 * Math.exp(-Math.max(0, y - 8) / 26) + 0.1 * lampTerm(x, y, 140) - 0.05 * smooth(0.6, 1, Math.abs(x - 240) / 240);
      v += (vnoise(x / 9, d / 2, 11) - 0.5) * 0.08;
      if (CEIL_SEAMS.some(s => Math.abs(d - s) < 0.55)) v -= 0.14;
      p.set(x, y, pickS(HONEY, v * 0.72, x, y));
    } else if (y < g) {                                               // principal rafter along the edge
      const k = y - (g - 5);
      const v = [0.16, 0.26, 0.32, 0.36, 0.5][Math.min(4, Math.floor(k))] + 0.25 * lampTerm(x, y, 130);
      p.set(x, y, pickS(WOOD, v + (vnoise(x / 7, y, 13) - 0.5) * 0.08, x, y));
    } else {
      p.set(x, y, plaster(x, y, wallV(x, y), 0));
    }
  }
  // exposed rafters in the ceiling planes, converging on the vanishing point
  for (const gx of [40, 84, 128]) for (const side of [0, 1]) {
    const x0 = side ? W - gx : gx, y0 = gableY(x0) - 3, dx = x0 - VP.x, dy = y0 - VP.y;
    let s = Math.min((0 - y0) / dy, dx < 0 ? (0 - x0) / dx : (W - 1 - x0) / dx);
    const x1 = x0 + dx * s, y1 = y0 + dy * s;
    thickLine(x0, y0, x1, y1, 4, 9, (x, y, u, a) => {
      if (y >= gableY(x) - 1) return;
      let v = 0.3 + 0.12 * (1 - Math.abs(a)) - 0.16 * smooth(0.55, 1, a * (side ? -1 : 1)) + 0.12 * Math.exp(-y / 30);
      v += (vnoise(u * 20, a * 3, 17 + gx) - 0.5) * 0.1;
      p.set(x, y, pickS(WOOD, v, x, y));
    });
  }
  // tie beam across the room (string lights hang from it)
  for (let y = BEAM.y0; y < BEAM.y1 + 2; y++) for (let x = 0; x < W; x++) {
    if (y >= BEAM.y1) { darken(p, x, y, y === BEAM.y1 ? 0.45 : 0.25); continue; }
    let v = [0.18, 0.26, 0.3, 0.33, 0.35, 0.36, 0.37, 0.42, 0.52][y] + 0.08 * lampTerm(x, y, 160) - 0.08 * smooth(0.6, 1, Math.abs(x - 240) / 240);
    v += (vnoise(x / 14, y / 1.2, 21) - 0.5) * 0.12 + (vnoise(x / 3, y / 0.8, 22) - 0.5) * 0.05;
    if (y > 1 && y < 7 && vnoise(x / 5, y / 2, 23) > 0.8) v -= 0.12;                    // knots
    p.set(x, y, pickS(WOOD, v, x, y));
  }
  for (const bx of [58, 422]) { p.fill(bx, 1, 4, 7, '#1a1614'); p.set(bx + 1, 2, '#5a524c'); p.set(bx + 1, 6, '#5a524c'); }   // iron straps

  // the embrasure: soffit, reveals (window light spills on them) and the deep sill
  fillPoly([[AL.x0, AL.y0], [AL.x1, AL.y0], [FR.x1, FR.y0], [FR.x0, FR.y0]], (x, y) => {
    const f = (y - AL.y0) / (FR.y0 - AL.y0);
    p.set(x, y, plaster(x, y, 0.3 + 0.1 * f, f > 0.5 ? (f - 0.5) * 2 : 0));
  });
  fillPoly([[AL.x0, AL.y0], [FR.x0, FR.y0], [FR.x0, FR.y1], [AL.x0, AL.y1]], (x, y) => {
    const f = (x - AL.x0) / (FR.x0 - AL.x0);
    p.set(x, y, plaster(x, y, 0.5 + 0.4 * lampTerm(x, y) - 0.08 * f, f > 0.55 ? (f - 0.55) * 2.2 : 0));
  });
  fillPoly([[AL.x1, AL.y0], [FR.x1, FR.y0], [FR.x1, FR.y1], [AL.x1, AL.y1]], (x, y) => {
    const f = (AL.x1 - x) / (AL.x1 - FR.x1);
    p.set(x, y, plaster(x, y, 0.36 + 0.2 * lampTerm(x, y) - 0.04 * f, f > 0.55 ? (f - 0.55) * 2.2 : 0));
  });
  fillPoly([[FR.x0, FR.y1], [FR.x1, FR.y1], [AL.x1, AL.y1], [AL.x0, AL.y1]], (x, y) => {
    const f = (AL.y1 - y) / (AL.y1 - FR.y1);                          // 1 at the glass, 0 at the front
    let v = 0.46 + 0.36 * lampTerm(x, y, 90) + (vnoise(x / 18, y / 0.9, 44) - 0.5) * 0.06 - 0.1 * f;
    const cl = f > 0.62 ? (f - 0.62) * 2.6 + (bayer(x, y) - 0.5) * 0.3 : 0;
    const col = cl > 0.66 ? pickS(COOLP, v * 0.9 + 0.1, x, y) : cl > 0.33 ? pickS(TWI, v * 0.9, x, y) : pickS(HONEY, v, x, y);
    p.set(x, y, col);
  });
  for (let y = AL.y0; y < AL.y1; y++) { p.set(AL.x0 - 1, y, PLASTER[8]); p.set(AL.x1, y, PLASTER[6]); }   // crisp opening edges
  for (let x = AL.x0; x < AL.x1; x++) p.set(x, AL.y0 - 1, PLASTER[6]);
  // sill front board and its shadow on the wall
  for (let y = AL.y1; y < AL.y1 + 4; y++) for (let x = AL.x0 - 4; x < AL.x1 + 4; x++) {
    if ((x === AL.x0 - 4 || x === AL.x1 + 3) && (y === AL.y1 || y === AL.y1 + 3)) continue;
    const v = [0.78, 0.55, 0.48, 0.28][y - AL.y1] + 0.3 * lampTerm(x, y, 90) - 0.12 * smooth(0.2, 1, Math.abs(x - 240) / 120);
    p.set(x, y, pickS(HONEY, v, x, y));
  }
  for (let x = AL.x0 - 4; x < AL.x1 + 4; x++) { darken(p, x, AL.y1 + 4, 0.35); darken(p, x, AL.y1 + 5, 0.18); }

  // the painted window frame: casing, sashes, muntins, meeting stiles
  for (let y = FR.y0; y < FR.y1; y++) for (let x = FR.x0; x < FR.x1; x++) {
    if (inPane(x, y)) { p.clear(x, y); continue; }
    let v = 0.58 + 0.28 * lampTerm(x, y, 110) - 0.1 * smooth(90, 36, y);
    const onMuntin = ROWS.findIndex(([, y1], k) => k < 2 && y >= y1 && y < y1 + 3);
    if (onMuntin >= 0 && x >= GL.x0 && x < GL.x1) v += [0.14, 0, -0.18][y - ROWS[onMuntin][1]];
    else if (x >= 237 && x < 242 && y >= GL.y0 && y < GL.y1) v += [0.14, 0.02, -0.3, 0.02, -0.14][x - 237];
    else {
      if (x === FR.x0 || y === FR.y0) v -= 0.25;
      else if (x === FR.x1 - 1 || y === FR.y1 - 1) v -= 0.3;
      else if (x === FR.x0 + 1 || y === FR.y0 + 1) v += 0.14;
      if (x === FR.x0 + 4 || x === FR.x1 - 5 || y === FR.y0 + 4) v -= 0.14;           // sash grooves
      if (y === FR.y1 - 3) v -= 0.12;
      if (x === GL.x0 - 1 || y === GL.y0 - 1) v -= 0.16;                          // putty line
      if (x === GL.x1 || y === GL.y1) v += 0.1;
    }
    if (hash(x, y, 61) < 0.04) v -= 0.08;                                        // worn paint
    p.set(x, y, pickS(CREAM, v, x, y, 0.5));
  }
  for (const [y0, y1] of ROWS) for (const [x0, x1] of COLS) {                     // putty shadow along each pane
    for (let x = x0; x < x1; x++) if (y0 > GL.y0) darken(p, x, y0 - 1, 0.12);
  }
  // espagnolette (brass bolt rod) with its handle, and hinges
  for (let y = GL.y0 + 2; y < GL.y1 - 1; y++) p.set(240, y, y % 2 ? '#a07a34' : '#8a6428');
  for (const gy of [GL.y0 + 3, GL.y1 - 5]) { p.fill(239, gy, 3, 2, '#b88c3c'); p.set(239, gy, '#e0bc68'); }
  p.fill(239, 128, 3, 11, '#9a7430'); p.fill(240, 129, 1, 9, '#d8b464'); p.set(239, 128, '#e8cc80'); p.set(241, 138, '#5a4018');
  p.fill(238, 131, 1, 5, '#7a5a26');
  for (const hy of [58, 164]) for (const hx of [FR.x0 + 1, FR.x1 - 3]) { p.fill(hx, hy, 2, 5, '#4a3622'); p.set(hx, hy, '#8a6a44'); }

  // floor: planks converging on the vanishing point, baseboard
  for (let y = FLOOR - 6; y < FLOOR; y++) for (let x = 0; x < W; x++) {
    const v = [0.55, 0.36, 0.3, 0.28, 0.24, 0.12][y - FLOOR + 6] + 0.2 * lampTerm(x, y, 120);
    p.set(x, y, pickS(WOOD, v, x, y));
  }
  for (let y = FLOOR; y < H; y++) for (let x = 0; x < W; x++) {
    const f = (FLOOR - VP.y) / (y - VP.y), bx = VP.x + (x - VP.x) * f;   // x where this line meets the wall
    const bi = Math.floor((bx + 600) / 17), fr = frac((bx + 600) / 17);
    let v = 0.42 + (hash(bi, 7) - 0.5) * 0.18 - (y - FLOOR) * 0.006 + 0.25 * lampTerm(x, y, 110);
    v += (vnoise(bx / 40, y / 1.5, 71 + bi) - 0.5) * 0.1;
    if (fr * 17 * (y - VP.y) / (FLOOR - VP.y) < 1) v -= 0.22;           // seam
    const end = FLOOR + 3 + Math.floor(hash(bi, 9) * 26);
    if (y === end) v -= 0.2;
    p.set(x, y, pickS(FLOORP, v, x, y));
  }
  // rug (muted terracotta, cream and teal), fringe at the far edge
  const RUGC = { dark: '#3e1a14', terra: '#72301f', terra2: '#8a3d26', cream: '#a88e6c', teal: '#1b3436', teal2: '#244644', ochre: '#8a5e26' };
  for (let y = 248; y < H; y++) for (let x = 60; x < 420; x++) {
    const rr = Math.hypot((x - 240) / 160, (y - 278) / 29);
    if (rr > 1.04) continue;
    let col;
    if (rr > 1) { if ((x & 1) === 0 && rr < 1.03) col = RUGC.cream; else continue; }
    else if (rr > 0.94) col = RUGC.dark;
    else if (rr > 0.9) col = RUGC.cream;
    else if (rr > 0.84) col = (Math.floor(x / 3) + y) % 4 === 0 ? RUGC.ochre : RUGC.terra2;
    else if (rr > 0.81) col = RUGC.cream;
    else col = (Math.abs((x - 240) % 14) + Math.abs(((y - 250) * 2) % 14) === 7 || (Math.abs(x - 240) % 14 === 7 && y % 4 === 0)) ? RUGC.ochre : ((x >> 1) + y) % 2 ? RUGC.teal : RUGC.teal2;
    p.set(x, y, col);
    if (y > 262) darken(p, x, y, (y - 262) * 0.04);
  }
}

/* ================= Furniture and props (painted into the room canvas) ================= */
const BOOKC = ['#6e2a22', '#94502c', '#a8803a', '#34503f', '#2a4254', '#5e5234', '#7e6e50', '#522a36', '#b8a47e',
  '#35354a', '#8a3a2e', '#4a6a5a'];
const LAMPC = ['#0b1511', '#12251d', '#1b372b', '#284e3d', '#386650', '#4f8068'];
const BRASS = ['#4a3010', '#7a5418', '#b08430', '#dcb458', '#f4dc90'];
function shadeCol(col, f) { return mixHex(col, f < 0 ? '#0c0608' : '#fff4dc', Math.abs(f)); }
function paintShelves(p) {
  const r = rng(303);
  for (const sh of [{ y: 110, x0: 14, x1: 100 }, { y: 144, x0: 18, x1: 98 }]) {
    for (let x = sh.x0; x < sh.x1; x++) {
      const lt = 0.2 * lampTerm(x, sh.y, 120);
      p.set(x, sh.y, pickS(WOOD, 0.6 + lt, x, sh.y)); p.set(x, sh.y + 1, pickS(WOOD, 0.5 + lt, x, sh.y + 1));
      p.set(x, sh.y + 2, pickS(WOOD, 0.36 + lt, x, sh.y + 2)); p.set(x, sh.y + 3, WOOD[2]);
      darken(p, x, sh.y + 4, 0.4); darken(p, x, sh.y + 5, 0.2);
    }
    for (const bx of [sh.x0 + 7, sh.x1 - 9]) for (let k = 0; k < 6; k++) { p.set(bx, sh.y + 4 + k, WOOD[3]); p.set(bx + 5 - k, sh.y + 4 + k, WOOD[2]); }
  }
  // upper shelf: a row of books, one leaning, a small succulent
  let x = 17;
  while (x < 82) {
    const w = 2 + Math.floor(r() * 3), h = 10 + Math.floor(r() * 7), col = BOOKC[Math.floor(r() * BOOKC.length)];
    const lean = x > 70 ? 1 : 0, band = 2 + Math.floor(r() * 3);
    for (let j = 0; j < h; j++) for (let k = 0; k < w; k++) {
      const y = 109 - j, xx = x + k + (lean ? Math.floor(j / 4) : 0);
      let f = k === w - 1 ? -0.35 : k === 0 ? 0.12 : 0;
      if (j === h - 1) f = 0.25;
      if (j === h - band || j === h - band - 1) f += j === h - band ? 0.3 : -0.1;
      p.set(xx, y, shadeCol(col, f - 0.15 + 0.2 * lampTerm(xx, y, 120)));
    }
    x += w + (r() < 0.15 ? 1 : 0) + (lean ? 3 : 0);
  }
  p.fill(85, 105, 7, 5, TERRA[3]); p.fill(86, 105, 5, 1, TERRA[5]); for (let y = 105; y < 110; y++) p.set(91, y, TERRA[1]);
  for (const [dx, dy, c] of [[0, 0, 4], [1, -1, 6], [2, -2, 5], [3, -3, 7], [4, -2, 6], [5, -1, 5], [6, 0, 4], [2, -1, 6], [3, -1, 7], [4, -1, 6], [3, -2, 6], [1, 0, 5], [5, 0, 5], [3, 0, 6]])
    p.set(85 + dx, 104 + dy, LEAF[c]);
  // lower shelf: stacked books, a framed photo, a jar of dried lavender
  [[22, 42, '#2a4254'], [24, 40, '#a8803a'], [21, 43, '#6e2a22']].forEach(([x0, x1, col], k) => {
    for (let xx = x0; xx < x1; xx++) { p.set(xx, 143 - k * 2, shadeCol(col, -0.25)); p.set(xx, 142 - k * 2, shadeCol(col, xx === x0 ? 0.3 : 0.05)); }
    p.set(x1 - 1, 142 - k * 2, '#d8c8a0'); p.set(x1 - 1, 143 - k * 2, '#b8a47e');
  });
  for (let y = 132; y < 144; y++) for (let xx = 48; xx < 58; xx++) {
    const edge = y === 132 || y === 143 || xx === 48 || xx === 57;
    p.set(xx, y, edge ? (xx === 57 || y === 143 ? WOOD[2] : WOOD[5]) : y < 138 ? pick(['#3a5a78', '#5a7a94'], (138 - y) / 6, xx, y) : pick(['#44603c', '#6a7a44'], (xx - 48) / 10, xx, y));
  }
  p.fill(64, 137, 5, 7, '#c8b894'); p.fill(64, 137, 1, 7, '#e0d4b0'); p.fill(68, 137, 1, 7, '#8a7a5c');
  for (const [lx, ly] of [[64, 131], [66, 129], [67, 132], [69, 130], [65, 133], [68, 134]]) { line(p, 66, 137, lx, ly, '#5a6a3a'); p.set(lx, ly, '#9a82c0'); p.set(lx, ly + 1, '#7a64a4'); }
}
function paintConsole(p) {
  fillPoly([[8, 198], [104, 198], [96, 207], [2, 207]], (x, y) => {
    const v = 0.44 + 0.28 * lampTerm(x, y, 110) + (vnoise(x / 16, y / 0.8, 81) - 0.5) * 0.12 + (y === 206 ? 0.18 : 0);
    p.set(x, y, pickS(WOOD, v, x, y));
  });
  fillPoly([[96, 207], [104, 198], [104, 228], [96, 236]], (x, y) => p.set(x, y, pickS(WOOD, 0.4 + 0.2 * lampTerm(x, y, 110), x, y)));
  for (let y = 207; y < 236; y++) for (let x = 2; x < 96; x++) {
    let v = 0.3 + 0.2 * lampTerm(x, y, 120) + (vnoise(x / 20, y / 0.9, 82) - 0.5) * 0.08;
    if (y === 207) v += 0.2;
    if (y >= 233) v -= 0.12;
    if (x === 48 || x === 49) v -= 0.14;
    const inOpen = x >= 6 && x < 46 && y >= 211 && y < 232, inDoor = x >= 52 && x < 92 && y >= 211 && y < 232;
    if (inOpen) {                                                   // open compartment full of records
      const k = x - 7, sp = hash(k, 3);
      let col = WOOD[0];
      if (k >= 0 && k < 38 && y > 212 + (sp < 0.2 ? 3 : 0) && sp > 0.08) col = shadeCol(BOOKC[Math.floor(hash(k, 4) * BOOKC.length)], -0.45 + 0.25 * hash(k, 5) - (y - 212) * 0.012);
      if (y === 211) col = WOOD[0];
      p.set(x, y, col); continue;
    }
    if (inDoor && (x === 54 || x === 89 || y === 213 || y === 229)) v -= 0.1;
    p.set(x, y, pickS(WOOD, v, x, y));
  }
  p.fill(56, 220, 2, 2, BRASS[2]); p.set(56, 220, BRASS[4]);
  for (const lx of [5, 89]) for (let y = 236; y < 246; y++) { const w = y < 241 ? 3 : 2; p.fill(lx + (y < 241 ? 0 : 1), y, w, 1, y === 236 ? WOOD[5] : WOOD[3]); }
  for (let y = 240; y < 249; y++) for (let x = 0; x < 106; x++) if (y > 245 || x > 3) darken(p, x, y, 0.3 * (1 - (y - 240) / 10) * clamp((104 - x) / 10, 0, 1));
}
function paintTurntable(p) {
  for (let y = 183; y < 201; y++) for (let x = 10; x < 71; x++) {
    if ((x === 10 || x === 70) && (y === 183 || y === 200)) continue;
    let v;
    if (y >= 197) v = y === 197 ? 0.3 : 0.14;                                    // plinth front
    else v = 0.5 + 0.2 * lampTerm(x, y, 110) + (vnoise(x / 12, y / 1.5, 85) - 0.5) * 0.1 + (y === 183 ? 0.15 : 0);
    p.set(x, y, y >= 197 ? pickS(WOOD, v, x, y) : pickS(HONEY, v * 0.85, x, y));
  }
  for (let y = 183; y < 198; y++) for (let x = 18; x < 56; x++) {
    const rr = Math.hypot((x + 0.5 - TT.cx) / (TT.rx + 1), (y + 0.5 - TT.cy) / (TT.ry + 0.6));
    if (rr > 1) continue;
    if (rr > 0.93) { p.set(x, y, y > TT.cy ? (x < TT.cx ? '#c8c8d2' : '#8a8a96') : '#5a5a66'); continue; }   // platter rim
    const r2 = Math.hypot((x + 0.5 - TT.cx) / TT.rx, (y + 0.5 - TT.cy) / TT.ry);
    let col = frac(r2 * 7) < 0.22 ? '#1c1b24' : '#0f0e14';
    if (Math.abs((x - TT.cx) + (y - TT.cy) * 2.6 + 4) < 1.3 && r2 > 0.4) col = '#34333f';   // static sheen
    if (r2 < 0.32) col = r2 > 0.26 ? '#8a3a1c' : '#c25a28';                           // label
    p.set(x, y, col);
  }
  p.set(TT.cx, TT.cy, '#d8d8e0');
  p.fill(60, 184, 4, 3, '#9a9aa6'); p.fill(60, 184, 4, 1, '#d0d0da'); p.fill(64, 182, 3, 3, '#3a3a44'); p.set(64, 182, '#6a6a78');
  line(p, 61, 186, 49, 191, '#c8c8d4'); line(p, 61, 187, 50, 191, '#7a7a88');
  p.fill(46, 191, 3, 2, '#2a2a34'); p.set(46, 191, '#8a8a98');
  p.set(14, 194, '#b0b0bc'); p.set(17, 194, '#b0b0bc'); p.set(14, 195, '#6a6a76'); p.set(17, 195, '#6a6a76');
  // record sleeves leaning on the wall (invented abstract covers)
  for (let y = 180; y < 198; y++) for (let x = 0; x < 17; x++) {
    const xx = 79 + x + Math.floor((198 - y) / 9);
    let col = '#1e3a42';
    if (Math.hypot(x - 8, y - 187) < 4.3) col = y < 187 ? '#e08a3e' : '#c8662e';
    if (y === 193 && x > 2 && x < 14) col = '#b8a47e';
    if (x === 16 || y === 197) col = '#10202a';
    p.set(xx, y, shadeCol(col, -0.1));
  }
  for (let y = 182; y < 199; y++) for (let x = 0; x < 17; x++) {
    const xx = 74 + x - Math.floor((199 - y) / 8);
    let col = '#a86a5e';
    if (y > 190 && Math.hypot(x - 8, y - 197) < 6.5) col = '#d8c8a2';
    if (y === 186 && x > 2 && x < 10) col = '#3a2224';
    if (x === 0) col = '#d8c8a0'; if (x === 16 || y === 198) col = '#6e5e44';
    p.set(xx, y, shadeCol(col, -0.2 + 0.25 * lampTerm(xx, y, 110)));
  }
}
function paintCanvasArt(p, x0, y0, w, h) {
  const cut = u => 0.6 + 0.05 * Math.sin(u * 0.9) + 0.04 * (hash(u, 91) - 0.5);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const x = x0 + i, y = y0 + j, u = i / w, v = j / h;
    let col;
    const bx = i >> 1;                                                // 2x1 "brush" blocks
    if (u < cut(j)) {
      const tx = Math.abs(i - Math.round(w * 0.33)), hm = (Math.round(h * 0.67) - j) * 12.5;   // painted tower, 12.5 m a row
      let roof = 0.62 + 0.05 * Math.sin(i * 0.7) + (hash(Math.floor(i / 5), 92) < 0.4 ? -0.05 : 0);
      if (tx < 8) roof = Math.max(roof, 0.66);                                         // the roofs dip under it, as in the view
      if (v < roof) col = pick(['#141c40', '#1c2750', '#27345e', '#34436c', '#46577c'], v / roof + (hash(bx, j, 93) - 0.5) * 0.25, bx, j);
      else col = pick(['#161a2c', '#232a40', '#323c56', '#465270'], 0.7 - (v - roof) * 1.4 + (hash(bx, j, 94) - 0.5) * 0.3, bx, j);
      if (v > roof && v < roof + 0.05 && hash(i, 95) < 0.3) col = '#c0643a';          // chimney pots
      if (v > roof + 0.12 && hash(i, j, 96) < 0.03) col = '#e8b060';                   // lit windows
      if (v < roof && hm > 0 && hm < 330) {                                            // the gold tower: arch, floors, spire
        const half = hm < 57.6 ? 62.5 * Math.exp(-hm / 90) / 12.5 : hm < 115.7 ? 33 * Math.exp(-(hm - 57.6) / 121) / 12.5
          : 0.5 + Math.pow(Math.max(0, 276 - hm) / 160, 0.8);
        const arch = hm < 52 ? Math.min(half - 1.5, 3.2 * Math.sqrt(1 - (hm / 52) ** 2)) : 0;
        const floor = Math.abs(hm - 57.6) < 6.25 ? 1 : Math.abs(hm - 115.7) < 6.25 ? 0.5 : 0;
        if (tx <= half + floor + 0.3 && tx >= arch - 0.2) col = floor || (tx === 0 && hm > 100) ? '#ffe08a' : '#e0a030';
      }
    } else {
      col = pick(['#b8a684', '#cbb994', '#d6c6a2'], 0.6 + 0.3 * (1 - u) - v * 0.3, i, j);   // bare canvas
      const roofL = Math.round(h * (0.62 + 0.05 * Math.sin(i * 0.7)));
      if (j === roofL && i % 3 !== 2) col = '#8a7c66';                                     // pencil lines
      if (Math.abs(Math.hypot(i - w * 0.78, j - h * 0.22) - 4) < 0.5) col = '#8a7c66';     // the moon, sketched
      if (i === w - 5 && j > h * 0.3 && j < h * 0.36) col = '#3a4a7a';                     // test dabs
      if (i === w - 4 && j > h * 0.4 && j < h * 0.45) col = '#d8883e';
    }
    p.set(x, y, shadeCol(col, -0.08 - 0.12 * u));
  }
  for (let j = 0; j < h; j++) p.set(x0 + w, y0 + j, '#6a5a44');                      // stretcher edge
}
function paintEasel(p) {
  thickLine(414, 104, 418, 247, 2, 3, (x, y, s, a) => p.set(x, y, pickS(HONEY, 0.22 + 0.06 * (1 - Math.abs(a)), x, y)));
  paintCanvasArt(p, EASEL.x0, EASEL.y0, EASEL.w, EASEL.h);
  for (const [xa, xb] of [[402, 380], [423, 446]]) thickLine(xa, 96, xb, 257, 3, 4, (x, y, s, a) => {
    if (x >= EASEL.x0 && x <= EASEL.x0 + EASEL.w && y >= EASEL.y0 && y < EASEL.y0 + EASEL.h) return;
    p.set(x, y, pickS(HONEY, 0.36 + (a < -0.2 ? 0.12 : a > 0.4 ? -0.1 : 0) - s * 0.08, x, y));
  });
  for (let y = 94; y < 121; y++) for (let x = 409; x < 416; x++) {
    if (y > EASEL.y0 + 1 && (x < 411 || x > 413)) continue;
    p.set(x, y, pickS(HONEY, x === 409 ? 0.5 : x === 415 ? 0.22 : 0.36, x, y));
  }
  for (let y = 176; y < 181; y++) for (let x = 378; x < 450; x++) p.set(x, y, pickS(HONEY, [0.55, 0.45, 0.32, 0.3, 0.18][y - 176], x, y));
  for (let x = 386; x < 440; x++) p.set(x, 215, HONEY[2]);
  for (let y = 250; y < 260; y++) for (let x = 370; x < 456; x++) darken(p, x, y, 0.22 * Math.max(0, 1 - Math.abs(x - 413) / 44));
}
function paintStool(p) {
  for (let y = 248; y < 262; y++) for (let x = 420; x < 480; x++) darken(p, x, y, 0.28 * Math.max(0, 1 - Math.hypot((x - 451) / 28, (y - 256) / 6)));
  thickLine(451, 221, 452, 252, 2, 2, (x, y) => p.set(x, y, HONEY[2]));
  for (const [xa, xb] of [[435, 430], [467, 472]]) thickLine(xa, 220, xb, 258, 3, 3, (x, y, s, a) => p.set(x, y, pickS(HONEY, 0.42 + (a < 0 ? 0.08 : -0.08) - s * 0.1, x, y)));
  for (let x = 432; x < 471; x++) { p.set(x, 243, HONEY[3]); p.set(x, 244, HONEY[1]); }
  for (let y = 211; y < 223; y++) for (let x = 429; x < 474; x++) {
    const rr = Math.hypot((x + 0.5 - 451) / 22, (y + 0.5 - 216) / 5);
    if (y < 220 && rr > 1) continue;
    if (y >= 220 && Math.abs(x + 0.5 - 451) > 22 * Math.sqrt(Math.max(0, 1 - ((219 - 216) / 5) ** 2))) continue;
    p.set(x, y, pickS(HONEY, y >= 220 ? 0.28 : 0.52 - rr * 0.1, x, y));
  }
  // jars: brushes in murky water, a second water jar, two paint tubes
  const jar = (x0, y0, w, hgt, water) => {
    for (let y = y0; y < y0 + hgt; y++) for (let x = x0; x < x0 + w; x++) {
      let col = y > y0 + 3 ? water : '#3a3c48';
      if (x === x0 || x === x0 + w - 1) col = '#6a7080';
      if (x === x0 + 1 && y > y0 + 1) col = '#a8b0c0';
      if (y === y0) col = '#8a92a4';
      p.set(x, y, col);
    }
  };
  [[437, 188, '#b83a2a'], [439, 186, '#2a2226'], [441, 190, '#c89a58'], [438, 191, '#3a5a8a']].forEach(([tx, ty, c]) => {
    line(p, 439, 206, tx, ty, c); p.set(tx, ty, '#1a1414'); p.set(Math.round(lerp(439, tx, 0.62)), Math.round(lerp(206, ty, 0.62)), '#b8b8c4');
  });
  jar(435, 202, 8, 13, '#40584e');
  jar(447, 205, 8, 10, '#6a4a32');
  p.fill(458, 212, 8, 2, '#d8d8e0'); p.fill(465, 212, 2, 2, '#2a4a9a'); p.fill(460, 214, 8, 1, '#c8c0b0'); p.set(467, 214, '#b83a2a');
}
function paintPinned(p) {
  for (const [x0, y0, w, h, seed] of [[371, 68, 16, 20, 1], [395, 61, 18, 22, 2]]) {
    for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) {
      let col = pick(['#9a8a6c', '#b0a080', '#c2b290'], 0.7 - (y - y0) / h * 0.4, x, y);
      const tx = x - x0 - 8, ty = y - y0;                                                    // the tower, in pencil
      if (seed === 1 && ty > 2 && ty < h - 2 && Math.abs(Math.abs(tx) - Math.round(((ty - 2) / (h - 4)) ** 2.2 * 6)) < 0.6) col = '#5a5048';
      if (seed === 1 && (ty === 12 || ty === 16) && Math.abs(tx) <= Math.round(((ty - 2) / (h - 4)) ** 2.2 * 6)) col = '#6a6054';
      if (seed === 2 && y - y0 === Math.round(12 + 3 * Math.sin((x - x0) * 0.8)) && x > x0 + 1 && x < x0 + w - 2) col = '#5a5048';   // rooftops
      if (seed === 2 && x - x0 === 11 && y - y0 > 4 && y - y0 < 12) col = '#6a5e52';
      p.set(x, y, col);
    }
    for (let y = y0 + 1; y <= y0 + h; y++) darken(p, x0 + w, y, 0.35);
    for (let x = x0 + 1; x <= x0 + w; x++) darken(p, x, y0 + h, 0.35);
    p.fill(x0 + 2, y0 - 1, 3, 2, '#d6cfb6'); p.fill(x0 + w - 5, y0 - 1, 3, 2, '#d6cfb6');
  }
}
function paintDesk(p) {
  for (const lx of [122, 356]) for (let y = 224; y < 242; y++) p.fill(lx, y, 3, 1, WOOD[2]);
  for (let y = 233; y < 258; y++) for (let x = 104; x < 377; x++) {                // shadow under the desk
    const f = y < FLOOR ? 0.55 : 0.5 * (1 - (y - FLOOR) / 18);
    darken(p, x, y, f * clamp((x - 104) / 6, 0, 1) * clamp((376 - x) / 6, 0, 1));
  }
  fillPoly([[DESK.bx0, DESK.top], [DESK.bx1, DESK.top], [DESK.fx1, DESK.front], [DESK.fx0, DESK.front]], (x, y) => {
    let v = 0.42 + 0.42 * lampTerm(x, y, 70) + (vnoise(x / 22, y / 0.7, 101) - 0.5) * 0.1 + (vnoise(x / 4, y / 0.5, 102) - 0.5) * 0.04;
    if (y === DESK.top) v -= 0.2;
    if (y === DESK.front - 1) v += 0.2;
    p.set(x, y, pickS(HONEY, v, x, y));
  });
  for (let y = DESK.front; y < DESK.bottom; y++) for (let x = DESK.fx0; x < DESK.fx1; x++) {
    let v = 0.34 + 0.22 * lampTerm(x, y, 90) - (y - DESK.front) * 0.012;
    if (y === DESK.bottom - 1) v -= 0.15;
    const dr = x >= 196 && x < 284 && y >= DESK.front + 1 && y < DESK.bottom - 1;
    if (dr && (x === 196 || x === 283 || y === DESK.front + 1 || y === DESK.bottom - 2)) v -= x === 196 || y === DESK.front + 1 ? 0.14 : -0.06;
    p.set(x, y, pickS(HONEY, v, x, y));
  }
  p.fill(239, 227, 2, 2, BRASS[2]); p.set(239, 227, BRASS[4]);
  for (const lx of [107, 369]) for (let y = DESK.bottom; y < 258; y++) {
    const w = y < 246 ? 4 : 3;
    for (let k = 0; k < w; k++) p.set(lx + k + (y < 246 ? 0 : lx < 200 ? 1 : 0), y, pickS(HONEY, (k === 0 ? 0.42 : k === w - 1 ? 0.18 : 0.3) + 0.15 * lampTerm(lx, y, 90), lx + k, y));
  }
}
function paintDeskItems(p) {
  // two stacked books at the right end
  for (let y = 209; y < 214; y++) for (let x = 334; x < 359; x++) p.set(x, y, y === 209 ? '#3e6a66' : y === 213 ? '#16282a' : x > 355 ? '#d8c8a2' : '#27464a');
  for (let y = 205; y < 209; y++) for (let x = 337; x < 356; x++) p.set(x, y, y === 205 ? '#8a3a44' : y === 208 ? '#3a141a' : x > 352 ? '#cdbc96' : '#5e2430');
  // open sketchbook under the lamp: a little cat sketched on the left page, roofs on the right
  fillPoly([[171, 214.5], [228, 214.5], [233, 222.5], [166, 222.5]], (x, y) => p.set(x, y, '#1a1416'));
  fillPoly([[172, 215], [199, 215], [199, 222], [168, 222]], (x, y) => p.set(x, y, pick(['#b8a88a', '#dccdb0', '#f2e6cc'], 0.95 - (x > 195 ? 0.4 : 0) - (y - 215) * 0.04, x, y)));
  fillPoly([[200, 215], [227, 215], [231, 222], [200, 222]], (x, y) => p.set(x, y, pick(['#b8a88a', '#dccdb0', '#f2e6cc'], 0.85 - (x < 203 ? 0.35 : 0) - (x - 200) * 0.01, x, y)));
  for (const [x, y] of [[178, 219], [179, 218], [180, 217], [181, 217], [182, 217], [183, 217], [184, 218], [185, 219], [178, 220], [185, 220], [186, 220], [179, 216], [181, 216],
    [206, 219], [207, 218], [208, 218], [209, 219], [210, 218], [211, 217], [212, 217], [213, 218], [214, 219], [215, 218], [216, 218], [217, 219], [218, 219], [211, 216], [211, 215], [208, 220], [214, 220]])
    p.set(x, y, '#6e6456');
  line(p, 236, 219, 249, 216, '#d8a830'); p.set(249, 216, '#2a2226'); p.set(236, 219, '#d88a8a'); line(p, 237, 220, 248, 217, '#9a7418');
  line(p, 238, 222, 251, 220, '#b84430'); p.set(251, 220, '#2a2226');
  p.fill(254, 220, 4, 1, '#1a1618'); p.fill(222, 216, 3, 2, '#d8c8c0');
  // cafe au lait bowl: wide and low, cream glaze with a blue band, coffee inside
  const bx = BOWL.x, by = BOWL.y;
  for (let y = by - 3; y < by + 8; y++) for (let x = bx - 10; x <= bx + 10; x++) {
    const dx = x + 0.5 - bx;
    let col = null;
    if (y <= by) {
      const rr = Math.hypot(dx / 9.6, (y + 0.5 - by + 0.5) / 2.6);
      if (rr > 1) continue;
      col = rr > 0.8 ? (y < by - 1 ? '#f6ecd6' : '#d6cab2') : (Math.hypot(dx + 2, (y + 0.5 - by + 0.5) * 2) < 3.2 ? '#dcbe92' : '#a06e3c');
    } else {
      const hw = y >= by + 6 ? 5 : 9.4 - (y - by) * 0.75;
      if (Math.abs(dx) > hw) continue;
      const lit = (dx / hw + 1) / 2;
      col = pick(['#ece0c6', '#c8ba9e', '#948670', '#5e5446'], lit * 0.9 + (y >= by + 6 ? 0.35 : 0), x, y);
      if (y === by + 2) col = lit < 0.55 ? '#4a6c9e' : '#2c4470';
    }
    p.set(x, y, col);
  }
  for (let x = bx - 8; x <= bx + 8; x++) darken(p, x, by + 8, 0.3);
}
function paintLamp(p) {
  // base, arms, joints (dark green enamel with brass), then the shade
  for (let y = 215; y < 222; y++) for (let x = 117; x < 136; x++) {
    const rr = Math.hypot((x + 0.5 - 126.5) / 9, (y + 0.5 - 218) / 2.8);
    if (rr > 1 && !(y >= 218 && y < 221 && Math.abs(x + 0.5 - 126.5) < 9)) continue;
    p.set(x, y, y >= 219 ? (y === 220 ? LAMPC[0] : LAMPC[2]) : rr < 0.5 ? LAMPC[4] : LAMPC[3]);
  }
  p.fill(125, 214, 3, 2, BRASS[2]);
  thickLine(126.5, 215, 120.5, 193, 2, 2, (x, y, s, a) => p.set(x, y, a < 0 ? LAMPC[4] : LAMPC[2]));
  thickLine(120.5, 193, 145.5, 171, 2, 2, (x, y, s, a) => p.set(x, y, a < 0 ? LAMPC[2] : LAMPC[4]));
  line(p, 124, 212, 119, 196, '#7a7a84');
  for (const [jx, jy] of [[120, 192], [144, 170]]) { p.fill(jx, jy, 3, 3, BRASS[2]); p.set(jx, jy, BRASS[4]); p.set(jx + 2, jy + 2, BRASS[0]); }
  const ax = 146, ay = 170, cx = 157, cy = 183, L = Math.hypot(cx - ax, cy - ay), ux = (cx - ax) / L, uy = (cy - ay) / L, nx = uy, ny = -ux;
  fillPoly([[ax - nx * 2.5, ay - ny * 2.5], [ax + nx * 2.5, ay + ny * 2.5], [cx + nx * 7.5, cy + ny * 7.5], [cx - nx * 7.5, cy - ny * 7.5]], (x, y) => {
    const s = ((x + 0.5 - ax) * ux + (y + 0.5 - ay) * uy) / L, a = ((x + 0.5 - ax) * nx + (y + 0.5 - ay) * ny) / lerp(2.5, 7.5, s);
    let k = a > 0.45 ? 4 : a > 0 ? 3 : a > -0.6 ? 2 : 1;
    if (a > 0.25 && a < 0.5 && s > 0.3) k = 5;
    p.set(x, y, LAMPC[k]);
  });
  line(p, cx - nx * 7, cy - ny * 7, cx + nx * 7, cy + ny * 7, '#ffd890');                // glowing inner lip
  line(p, cx - nx * 6 + ux, cy - ny * 6 + uy, cx + nx * 6 + ux, cy + ny * 6 + uy, '#fff0c0');
  p.fill(Math.round(cx + ux), Math.round(cy + uy), 2, 2, '#fffbea');
}
function paintCushion(p) {
  const ccx = CAT.x + 26;
  for (let y = 196; y < 206; y++) for (let x = ccx - 24; x < ccx + 24; x++) {
    const cx = Math.abs(x + 0.5 - ccx), top = 197 + (cx > 22 ? 1 : 0);
    if (y < top || (cx > 23 && (y < 199 || y > 203))) continue;
    let v = y < 203 ? 0.62 - (y - top) * 0.04 : 0.3 - (y - 203) * 0.06;
    v += 0.2 * lampTerm(x, y, 100) - 0.1 * (cx / 24);
    if (y === 200 && (x === ccx - 12 || x === ccx || x === ccx + 12)) v -= 0.3;
    p.set(x, y, pickS(MUSTARD, v, x, y));
  }
  for (const tx of [ccx - 24, ccx + 23]) { p.set(tx, 204, MUSTARD[4]); p.set(tx, 205, MUSTARD[3]); p.set(tx, 206, MUSTARD[2]); }
}
function renderRoom() {
  const p = pix(W, H);
  paintShell(p);
  paintShelves(p);
  paintConsole(p);
  paintTurntable(p);
  paintPinned(p);
  paintEasel(p);
  paintStool(p);
  paintDesk(p);
  paintDeskItems(p);
  paintCushion(p);
  paintLamp(p);
  return p.done();
}

/* ================= The tuxedo cat (breathing poses, a stretch; near ear and tail tip drawn per frame) ================= */
const CATC = { k: '#16121a', k2: '#1e1924', kd: '#0e0b12', rimC: '#3c4568', rimW: '#5a3a2c', w: '#e4d6be', ws: '#ad9d88',
  wr: '#f7eeda', pink: '#d88a88', eye: '#4a3c3a', mouth: '#8a3a40' };
const CAT_POSES = {
  inhale: { body: [27, 11.4, 13, 6.1], hip: [35, 10.3, 7.5, 7.2], head: [13.5, 11, 6, 5], paws: [9, 16, 4.6, 1.6], tail: [19, 29] },
  exhale: { body: [27, 11.9, 13, 5.6], hip: [35, 10.9, 7.5, 6.6], head: [13.5, 11.2, 6, 5], paws: [9, 16, 4.6, 1.6], tail: [19, 29] },
  half: { body: [28, 11.6, 14, 5.8], hip: [36, 10.2, 7.5, 7.3], head: [12.5, 8.8, 6, 5], paws: [6, 16, 6.5, 1.6], tail: [21, 31] },
  full: { body: [28, 12.4, 15, 5], hip: [37, 9.4, 7.5, 8.1], head: [10.5, 8, 6, 5], paws: [4, 16.2, 6.5, 1.5], tail: [23, 33], yawn: true },
};
function renderCat(pose) {
  const w = 48, h = 19, p = pix(w, h);
  const inE = (x, y, e) => ((x + 0.5 - e[0]) / e[2]) ** 2 + ((y + 0.5 - e[1]) / e[3]) ** 2 <= 1;
  const [hx, hy] = pose.head;
  const fex = Math.round(hx - 3.5), fey = Math.round(hy - 7);
  const farEar = (x, y) => (y === fey && x === fex) || (y === fey + 1 && x >= fex - 1 && x <= fex) || (y === fey + 2 && x >= fex - 1 && x <= fex + 1);
  const bodyF = (x, y) => inE(x, y, pose.body) || inE(x, y, pose.hip);
  const S = (x, y) => y >= 0 && y < h && x >= 0 && x < w && (bodyF(x, y) || inE(x, y, pose.head) || inE(x, y, pose.paws) || farEar(x, y));
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!S(x, y)) continue;
    const head = inE(x, y, pose.head), paw = inE(x, y, pose.paws) && !head;
    const muzzle = head && ((x + 0.5 - (hx - 1.5)) / 3.6) ** 2 + ((y + 0.5 - (hy + 3)) / 2.4) ** 2 <= 1;
    const blaze = head && Math.abs(x + 0.5 - (hx - 1.2)) < 0.8 && y + 0.5 > hy - 1.5;
    const chest = !head && bodyF(x, y) && x < pose.body[0] - 7 && y > pose.body[1] - 0.5;
    const white = paw || muzzle || blaze || chest;
    const top = !S(x, y - 1), left = !S(x - 1, y);
    let col;
    if (white) col = top ? CATC.wr : y >= h - 2 || (paw && y === Math.round(pose.paws[1] + 1)) ? CATC.ws : CATC.w;
    else {
      col = y > pose.body[1] + 2 ? CATC.kd : CATC.k;
      if (hash(x, y, 7) < 0.12) col = CATC.k2;
      if (top) col = x > hx + 3 ? CATC.rimC : CATC.rimW;
      else if (left) col = CATC.rimW;
    }
    p.set(x, y, col);
  }
  // tail wrapped around the front, crossing the white chest, tip curled up
  for (let x = pose.tail[0] + 1; x <= pose.tail[1]; x++) { p.set(x, 17, CATC.kd); p.set(x, 16, x > pose.tail[1] - 3 ? CATC.kd : CATC.k); }
  p.set(pose.tail[0], 16, CATC.k); p.set(pose.tail[0], 15, CATC.k); p.set(pose.tail[0] - 1, 14, CATC.rimW); p.set(pose.tail[0], 14, CATC.rimC);
  // face: closed eyes, nose, whiskers; a yawn in the full stretch
  const ey = Math.round(hy - 1), ex = Math.round(hx);
  if (pose.yawn) { p.set(ex - 4, ey, CATC.eye); p.set(ex - 3, ey + 1, CATC.eye); p.set(ex + 1, ey + 1, CATC.eye); p.set(ex + 2, ey, CATC.eye); p.fill(ex - 3, ey + 3, 2, 2, CATC.mouth); p.set(ex - 3, ey + 3, CATC.pink); }
  else { p.set(ex - 4, ey, CATC.eye); p.set(ex - 3, ey + 1, CATC.eye); p.set(ex + 1, ey + 1, CATC.eye); p.set(ex + 2, ey, CATC.eye); }
  p.set(ex - 5, ey + 2, CATC.pink);
  p.set(ex - 7, ey + 2, '#8a8070'); p.set(ex - 8, ey + 3, '#6a6258');
  p.set(fex, fey + 1, CATC.pink); p.set(fex, fey, CATC.rimW);
  return p.done();
}
/** near ear drawn per frame so it can twitch */
function drawEar(t, pose, X, Y) {
  const [hx, hy] = pose.head, ex = X + Math.round(hx + 2), ey = Y + Math.round(hy - 7);
  const tw = EAR_TW.some(e => t >= e && t < e + 0.32);
  ctx.fillStyle = CATC.k;
  if (tw) { ctx.fillRect(ex - 1, ey + 2, 4, 1); ctx.fillRect(ex, ey + 1, 3, 1); ctx.fillStyle = CATC.rimC; ctx.fillRect(ex + 2, ey + 1, 1, 1); }
  else { ctx.fillRect(ex, ey, 1, 1); ctx.fillRect(ex - 1, ey + 1, 3, 1); ctx.fillRect(ex - 1, ey + 2, 4, 1); ctx.fillStyle = CATC.rimC; ctx.fillRect(ex, ey, 1, 1); ctx.fillStyle = CATC.pink; ctx.fillRect(ex, ey + 1, 1, 1); }
}

/* ================= Hanging pothos (drawn per frame in row strips so the vines sway) ================= */
function renderPlant(seed, hx, potY, ceramic, vines) {
  const x0 = hx - 26, y0 = BEAM.y1, w = 52, hgt = 124, p = pix(w, hgt), r = rng(seed);
  const L = (x, y, col) => p.set(x - x0, y - y0, col);
  for (const dx of [-6, 0, 6]) for (let y = y0 + 1; y < potY; y++) {
    const x = Math.round(lerp(hx, hx + dx, (y - y0) / (potY - y0)));
    L(x, y, dx === 0 ? '#6e624e' : dx < 0 ? '#c8b894' : '#a09070');
  }
  L(hx, y0 + 1, '#e0d0a8'); L(hx - 1, y0 + 2, '#c8b894'); L(hx + 1, y0 + 2, '#a09070');
  for (const dx of [-4, 4]) { L(hx + dx, potY - 5, '#d8c8a0'); L(hx + dx + (dx < 0 ? -1 : 1), potY - 4, '#b8a880'); }
  const potPal = ceramic ? ['#5a5046', '#8e8270', '#bcae94', '#dccfb4', '#efe4cc'] : TERRA.slice(1);
  for (let y = potY; y < potY + 9; y++) {
    const hw = y === potY ? 7 : 6 - Math.floor((y - potY) / 3);
    for (let x = hx - hw; x <= hx + hw; x++) {
      let v = 0.75 - (x - hx + hw) / (2 * hw) * 0.6 - (y - potY) * 0.02;
      if (y === potY) v = 0.95; else if (y === potY + 1) v -= 0.3;
      L(x, y, pick(potPal, v, x, y));
    }
  }
  const leaf = (x, y, v, flip) => {
    const shape = flip ? [[0, 0], [1, 0], [-1, 1], [0, 1], [1, 1], [0, 2]] : [[0, 0], [-1, 0], [-1, 1], [0, 1], [1, 1], [0, 2]];
    shape.forEach(([dx, dy], k) => L(x + dx, y + dy, LEAF[clamp(Math.round(v * 7) + (k === 0 ? 1 : k === 5 ? -2 : 0), 0, 7)]));
    if (r() < 0.25) L(x, y + 1, '#a6b45a');                             // variegation
  };
  for (let k = 0; k < 13; k++) leaf(hx - 9 + Math.floor(r() * 19), potY - 3 + Math.floor(r() * 4), 0.55 + r() * 0.35, r() < 0.5);
  for (const vn of vines) {
    let x = hx + vn.dx, side = r() < 0.5 ? -1 : 1;
    for (let y = potY + 2; y < potY + vn.len; y++) {
      const u = (y - potY) / vn.len;
      x = hx + vn.dx + Math.round(vn.bend * Math.sin(u * Math.PI * 0.9 + vn.ph));
      L(x, y, LEAF[2]);
      if ((y - potY) % vn.gap === 0) { leaf(x + side * 2, y, 0.4 + r() * 0.4 - u * 0.15, side > 0); side = -side; }
    }
    leaf(x, potY + vn.len, 0.5, r() < 0.5);
  }
  return { c: p.done(), x0, y0, pot: potY + 9 - y0, hgt };
}
function drawPlant(pl, t, k, ph) {
  const amp = 1.7, sw = wave(t, k, ph), len = pl.hgt - pl.pot;
  ctx.drawImage(pl.c, 0, 0, 52, pl.pot, pl.x0, pl.y0, 52, pl.pot);
  let y = pl.pot;
  while (y < pl.hgt) {
    const off = Math.round(amp * Math.pow((y - pl.pot) / len, 1.3) * sw);
    let y2 = y + 1;
    while (y2 < pl.hgt && Math.round(amp * Math.pow((y2 - pl.pot) / len, 1.3) * sw) === off) y2++;
    ctx.drawImage(pl.c, 0, y, 52, y2 - y, pl.x0 + off, pl.y0 + y, 52, y2 - y);
    y = y2;
  }
}

/* ================= Pigeons (a pair on the chimney stack across the street) ================= */
const PIG_KEY = { l: '#c4cadc', h: '#9aa0b6', b: '#2a2a32', n: '#5e9488', s: '#b2b8cc', m: '#7a7f96', w: '#4c5066', t: '#3a3e50', f: '#c07a6a', d: '#5a5e74' };
const PIG = {
  idle: ['........', '........', '.....ll.', '....hhhb', '.sssnn..', 'tsmmmmd.', '.tmwwmd.', '...f.f..'],
  peck: ['........', '........', '........', '........', '.sssnnl.', 'tsmmmnhb', '.tmwwmd.', '...f.f..'],
  flap: ['.w....w.', '..w..w..', '..ww.wll', '...wwhhb', '.sssnn..', 'tsmmmmd.', '.tmwwmd.', '...f.f..'],
};
function drawPigeons(t) {
  const { x, y } = PIGEONS;
  const bell = BELL.some(b => t >= b && t < b + 1.1);
  for (let k = 0; k < 2; k++) {
    const st = step(t, 480);
    let spr = hash(k, st, 41) < 0.18 ? PIG_SPR.peck : PIG_SPR.idle;
    if (bell && (k === 0 || t >= BELL[1])) spr = PIG_SPR.flap;
    const dx = k === 0 ? 0 : 8 + (t >= 100 && t < 162 ? -1 : 0);
    ctx.drawImage(k === 0 ? spr[0] : spr[1], x + dx, y - 8);
  }
}

/* ================= Hidden things: the mouse on the shelf, the balloon behind the roofs (the heart is in the mist) ================= */
const MOUSE_KEY = { b: '#6b584c', l: '#84705f', e: '#9a7068', k: '#120c0a', n: '#b8807a', t: '#8a6a62', f: '#4a3a32' };
const MOUSE_ROWS = {                                  // facing the succulent, its tail over the shelf's end; it sniffs the air now and then
  idle: ['..e.e....', '.bbbbl...', 'nbkbbbl..', '.bbbbbbt.', '..f..f.t.', '........t', '........t'],
  sniff: ['.e.e.....', 'nbkbbl...', '.bbbbbl..', '.bbbbbbt.', '..f..f.t.', '........t', '........t'],
};
const BALLOON_KEY = { r: '#b23a34', h: '#e2705c', d: '#7c2228', k: '#5a1a20' };
const BALLOON_ROWS = ['.rr.', 'rhrd', 'rrrd', '.dd.', '.k..'];
function drawMouse(t) {
  ctx.drawImage(frac((t + 6) / 15) < 0.11 ? MOUSE_SPR.sniff : MOUSE_SPR.idle, MOUSE.x, MOUSE.y);
}
/** a red balloon on a string, let up from a balcony across the street: it comes up behind the roof, sways a while and is drawn back down */
function drawBalloon(t) {
  const w = BALLOON_UP.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const tau = t - w[0], up = smooth(0, 3, tau) * (1 - smooth(w[1] - w[0] - 3, w[1] - w[0], tau));
  const dx = Math.round(wave(t, 30, 0.2) * 1.3 * up), y = BALLOON.y + Math.round((1 - up) * BALLOON.rise + wave(t, 48, 0.6) * 0.6);
  ctx.fillStyle = '#8a90ae';                                          // the string: it leans with the balloon, its foot stays put
  for (let j = 5; j < 5 + BALLOON.rise; j++) ctx.fillRect(BALLOON.x + 1 + Math.round(dx * Math.max(0, 1 - (j - 5) / 8)), y + j, 1, 1);
  ctx.drawImage(BALLOON_C, BALLOON.x + dx, y);
}

/* ================= Glass overlays: condensation at the bottom corners, beads along the rails ================= */
function renderGlassFog() {
  const p = pix(OW, OH);
  for (let y = GL.y0; y < GL.y1; y++) for (let x = GL.x0; x < GL.x1; x++) {
    if (!inPane(x, y)) continue;
    let f = 1.25 * Math.max(0, 1 - Math.hypot((x - GL.x0) / 70, (y - GL.y1) / 40)) + 1.25 * Math.max(0, 1 - Math.hypot((x - GL.x1) / 64, (y - GL.y1) / 36));
    const pane = PANES.find(q => x >= q.x0 && x < q.x1 && y >= q.y0 && y < q.y1);
    f += 0.35 * Math.max(0, 1 - (pane.y1 - y) / 5);                    // a thin misty line on every bottom rail
    f += (vnoise(x / 4, y / 3, 131) - 0.5) * 0.25;
    const q = Math.floor(clamp(f, 0, 1) * 5 + bayer(x, y) - 0.2) / 5;
    if (q <= 0) continue;
    const speck = hash(x, y, 133) < 0.06 * q;                          // micro-droplets in the mist
    p.set(x - OX, y - OY, speck ? '#e4ecfa' : '#aab6ce', Math.round(Math.min(1, q) * (speck ? 0.7 : 0.5) * 255));
  }
  // a heart someone drew in the mist with a finger (a hidden thing): the line is wiped clear, the mist round it is
  // even, and a drop has run down from its tip
  HEART_ROWS.forEach((row, j) => { for (let i = -1; i <= row.length; i++) {
    const x = HEART.x + i - OX, y = HEART.y + j - OY;
    if (row[i] === 'x') p.clear(x, y);
    else if (j < 6 && p.alpha(x, y) < 96) p.set(x, y, '#aab6ce', 96);
  } });
  return p.done();
}
const HEART_ROWS = ['.xx.xx.', 'x..x..x', 'x.....x', '.x...x.', '..x.x..', '...x...', '...x...', '...x...'];
function renderRailBeads() {
  const p = pix(OW, OH), r = rng(141);
  for (const q of PANES) {
    let x = q.x0 + 1 + Math.floor(r() * 5);
    while (x < q.x1 - 4) {
      if (r() < 0.28) { x += 4 + Math.floor(r() * 9); continue; }
      const w = r() < 0.22 ? 3 : r() < 0.55 ? 2 : 1, yb = q.y1 - 1;
      for (let j = 0; j < w; j++) for (let k = 0; k < w; k++) {
        if (w === 3 && j === 2 && k !== 1) continue;
        const top = j === w - 1 || (w === 3 && j === 1 && k !== 1);
        p.set(x + k - OX, yb - j - OY, top && k === 0 ? '#e2eafa' : j === 0 ? '#34425e' : '#9aaccc', top && k === 0 ? 200 : j === 0 ? 150 : 110);
      }
      x += w + 1 + Math.floor(r() * 5);
    }
  }
  return p.done();
}

/* ================= Lamp light: cone to the desk, pool on the sketchbook; vignette ================= */
function renderCone() {
  const p = pix(W, H), c = C('#ffb458');
  fillPoly([[151, 188], [163, 178], [232, 224], [158, 224]], (x, y) => {
    const d = Math.hypot(x - LAMP.x, y - LAMP.y), q = Math.floor(Math.max(0, 1 - d / 62) * 4 + bayer(x, y)) / 4;
    if (q > 0) p.set(x, y, c, Math.round(Math.min(1, q) * (inPane(x, y) ? 0.07 : 0.15) * 255));
  });
  const c2 = C('#ffd89a');
  for (let y = 210; y < 226; y++) for (let x = 150; x < 250; x++) {
    const d = Math.hypot((x - 194) / 42, (y - 219) / 7), q = Math.floor(Math.max(0, 1 - d) * 4 + bayer(x, y)) / 4;
    if (q > 0) p.set(x, y, c2, Math.round(Math.min(1, q) * 0.2 * 255));
  }
  return p.done();
}
function renderVignette() {
  const p = pix(W, H), c = [8, 4, 6];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.62, 1.4, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.45) / (H / 2) * 0.95));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.5 * 255));
  }
  return p.done();
}

/* ================= Build-time particle tables ================= */
const BEADS = [];                                     // raindrops clinging to the glass
{ const r = rng(501); for (let i = 0; i < 200; i++) { const u = r(); BEADS.push({ id: i, n: 3 + Math.floor(r() * 7), ph: r(), s: u < 0.55 ? 1 : u < 0.9 ? 2 : 3 }); } }
const RUNNERS = [];                                   // drops that slide down in bursts, merge and pool
{ const r = rng(502); for (let i = 0; i < 30; i++) RUNNERS.push({ id: 1000 + i, n: 4 + Math.floor(r() * 5), ph: r() }); }
const ORAIN = [];                                     // blurred rain outside, two depths
{ const r = rng(601); for (let i = 0; i < 180; i++) { const far = i < 115; ORAIN.push({ id: i, n: far ? 150 + Math.floor(r() * 40) : 205 + Math.floor(r() * 40), ph: r(), far }); } }
const MOTES = [];                                     // dust in the lamp light, three depths
{ const r = rng(701); for (let i = 0; i < 70; i++) MOTES.push({ id: i, layer: i < 32 ? 0 : i < 58 ? 1 : 2, n: 2 + Math.floor(r() * 3), ph: r(), k1: 5 + Math.floor(r() * 9), k2: 6 + Math.floor(r() * 11), p1: r(), p2: r() }); }
const BULBS = [];                                     // string lights along the beam
const NAILS = [16, 82, 148, 212, 268, 332, 398, 464];
for (let s = 0; s < NAILS.length - 1; s++) {
  const a = NAILS[s], b = NAILS[s + 1], sag = 7 + (s % 2) * 2;
  for (let k = 1; k < 7; k++) { const u = k / 7; BULBS.push({ x: Math.round(lerp(a, b, u)), y: Math.round(BEAM.y1 + 1 + sag * 4 * u * (1 - u)), c: (s * 6 + k) % 3, k: 6 + ((s * 7 + k * 5) % 11), ph: frac(s * 0.37 + k * 0.61) }); }
}
const BULB_COL = ['#ffd27a', '#ffb45e', '#ffe8b4'];
const BB_RAIN = [];                                   // raindrops lit by the billboard's floodlamps
{ const r = rng(911); for (let i = 0; i < 14; i++) BB_RAIN.push({ id: 3000 + i, n: 1500 + Math.floor(r() * 700), ph: r() }); }
// a picked weather (site/js/engine/weather.js): Paris draws its own rain; any other pick falls outside only, behind the glass
// (far roofs, the zinc sea, past the sill to the street), and catches the tower, the moon, the neighbours' windows and
// the desk lamp shining out through the glass
const WEATHER = weatherPlans(['rain']);
const WX_LAND = [[134, 148], [150, 168], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: TOWER.x, y: TOWER.base - 22, rx: 13, ry: 25, col: '#ffc868' }, { x: MOON.x, y: MOON.y, rx: 12, ry: 12, col: '#dce2f4' },
  ...WINS.map(w => ({ x: w.x + 5, y: w.y + 6, rx: 9, ry: 10, col: w.kind === 'tv' ? '#8aa4ff' : '#ffb860' })),
  { x: 170, y: 178, rx: 30, ry: 22 },
];
const WX_DENSITY = 1.8;                               // only the window shows the sky, so more of it falls there
// no tree stands outside a seventh-floor window: the wind carries the leaves across the view, over the roofs, and
// none comes past the glass close by
const WX_LEAVES = { wind: [GL.x0, 46, GL.x1, 140] };
const WX = { land: WX_LAND, lights: WX_LIGHTS, density: WX_DENSITY, leaves: WX_LEAVES };
const WX_BB = { land: WX_LAND, density: WX_DENSITY, leaves: WX_LEAVES, lights: [...WX_LIGHTS, ...BB_LAMPS.map(lx => ({ x: lx, y: BB_LENS + 9, rx: 5, ry: 10, col: '#ffe2b0' }))] };   // + the floodlamps
const NB = { warm: '#c88c48', warmHi: '#e4aa62', rose: '#c06246', roseHi: '#dc8858', orange: '#b4662e', dark: '#10162a', sil: '#2c1a12', sil2: '#1c100a',
  plant: '#3e6a32', pot: '#8a4a2a', page: '#eadab4', curtain: '#9a5a30', tv: ['#243a78', '#34509a', '#1e2c64', '#4a68b2', '#18244e', '#5a7cc4'] };

/* ================= Prerendered state ================= */
let HL, SKY_C, MOON_C, CLOUDS, TOWER_C, FAR, MID, NEAR, ROOM, FOG_C, RAIL_C, CONE_C, VIG_C, CAT_SPR, PLANT_L, PLANT_R,
  PIG_SPR, PUFF_S, PUFF_W, PUFF_D, LG, SMOKE_FAR, SMOKE_NEAR, BB_FRAME, BB_FACE, BB_FACE_DIM, PITCH, MOUSE_SPR, BALLOON_C;
const LGW = W >> 2, LGH = H >> 2;
function prerender() {
  HL = {
    moon: makeHalo(44, '#7890cc', 0.2, 1.6), moonCore: makeHalo(12, '#f2eed8', 0.34, 1.5), moonHaze: makeHalo(24, '#b8c4e8', 0.1, 1.2),
    tower: makeHalo(32, '#ffa838', 0.16, 1.5), towerCore: makeHalo(12, '#ffd070', 0.22), spark: makeHalo(3, '#ffffff', 0.55, 1),
    red: makeHalo(4, '#ff3a2a', 0.6, 1), beacon: makeHalo(12, '#fff2d0', 0.55, 1.3), win: makeHalo(11, '#ffb860', 0.2), tv: makeHalo(11, '#6a8cff', 0.26),
    strobe: makeHalo(3, '#ffffff', 0.7, 1), lamp: makeHalo(84, '#ffa850', 0.15, 1.7), lampCore: makeHalo(18, '#fff0c8', 0.4, 1.4),
    bulb: makeHalo(7, '#ffbe6a', 0.34, 1.3), led: makeHalo(3, '#ff5a30', 0.5, 1), refl: makeHalo(16, '#ffb070', 0.1, 1.4), far: makeHalo(4, '#ffc070', 0.25, 1),
    bbLamp: makeHalo(6, '#ffe0a0', 0.28, 1.3), bbGlow: makeHalo(34, '#ff9a5a', 0.05, 1.5),
  };
  BB_FRAME = renderBillboardFrame();
  BB_FACE = renderBillboardFace([1, 1, 1]); BB_FACE_DIM = renderBillboardFace([1, 1, 0]);
  PITCH = makePitch(BB.w, BB.h, { accent: '#f4a261' });
  SKY_C = renderSky(); MOON_C = renderMoon();
  CLOUDS = [
    { c: makeClouds(3, 42, { top: '#2e3a66', body: '#1b2448', core: '#161e3e', bot: '#27305a' }, 0.5, 1, 1), y: 38, k: 1, a: 0.92 },
    { c: makeClouds(9, 30, { top: '#36446f', body: '#222c52', core: '#1c2548', bot: '#34406a' }, 0.56, 0.8, 0.7), y: 70, k: 2, a: 0.8 },
  ];
  TOWER_C = renderTower(); FAR = renderFar(); MID = renderMid(); NEAR = renderNear();
  ROOM = renderRoom();
  FOG_C = renderGlassFog(); RAIL_C = renderRailBeads(); CONE_C = renderCone(); VIG_C = renderVignette();
  CAT_SPR = {}; for (const k in CAT_POSES) CAT_SPR[k] = renderCat(CAT_POSES[k]);
  PLANT_L = renderPlant(811, 113, 32, false, [{ dx: -6, len: 58, bend: 2, ph: 0, gap: 4 }, { dx: -2, len: 80, bend: 3, ph: 1, gap: 5 }, { dx: 3, len: 44, bend: 2, ph: 2, gap: 4 }, { dx: 6, len: 68, bend: 3, ph: 0.5, gap: 5 }]);
  PLANT_R = renderPlant(823, 367, 30, true, [{ dx: -5, len: 66, bend: 3, ph: 0.3, gap: 5 }, { dx: 0, len: 40, bend: 2, ph: 1.4, gap: 4 }, { dx: 5, len: 84, bend: 3, ph: 2.2, gap: 5 }]);
  PIG_SPR = {};
  for (const k in PIG) {
    const a = sprite(PIG[k], PIG_KEY), [b, bx] = makeCanvas(a.width, a.height);
    bx.translate(a.width, 0); bx.scale(-1, 1); bx.drawImage(a, 0, 0);
    PIG_SPR[k] = [a, b];
  }
  MOUSE_SPR = { idle: sprite(MOUSE_ROWS.idle, MOUSE_KEY), sniff: sprite(MOUSE_ROWS.sniff, MOUSE_KEY) };
  BALLOON_C = sprite(BALLOON_ROWS, BALLOON_KEY);
  PUFF_S = [null, 1, 2, 3].map(r => r && makePuff(r, '#9ea8c0', 1));
  PUFF_W = [null, 1, 2].map(r => r && makePuff(r, '#f4ead8', 1));
  PUFF_D = makePuff(1, '#ffe2b0', 0.8);
  SMOKE_FAR = MID.smoke.map(([x, y], i) => ({ x, y, ph: hash(i, 3) }));
  SMOKE_NEAR = NEAR.smoke.filter((s, i) => i === 1 || i === 3).map(([x, y], i) => ({ x, y, ph: 0.3 + i * 0.4 }));
  LG = new Float32Array(LGW * LGH);                                  // coarse light map for the dust motes
  const cone = [[146, 186], [166, 174], [246, 226], [150, 226]];
  for (let j = 0; j < LGH; j++) for (let i = 0; i < LGW; i++) {
    const x = i * 4 + 2, y = j * 4 + 2;
    let v = 0.55 * lampTerm(x, y, 38);
    if (inPoly(x, y, cone)) v += 0.75 * Math.max(0, 1 - Math.hypot(x - LAMP.x, y - LAMP.y) / 75);
    if (y < 40) v += 0.25 * Math.exp(-(y - 12) / 14);
    LG[j * LGW + i] = Math.min(1, v);
  }
}
const lightAt = (x, y) => { const i = x >> 2, j = y >> 2; return i < 0 || j < 0 || i >= LGW || j >= LGH ? 0 : LG[j * LGW + i]; };

/* ================= Per-frame: sky, moon, clouds, plane ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, OX, OY);
  ctx.fillStyle = '#dfe4f6';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9);
  ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7);
  for (const L of CLOUDS) {
    const off = Math.floor(frac(L.k * t / LOOP) * CW);
    ctx.globalAlpha = L.a;
    for (let x = OX - CW + off; x < OX + OW; x += CW) ctx.drawImage(L.c, x, L.y);
  }
  ctx.globalAlpha = 0.55; ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7);         // the moon shows through thin cloud
  ctx.globalAlpha = 1;
  halo(HL.moonCore, MOON.x, MOON.y, 0.8); halo(HL.moonHaze, MOON.x, MOON.y, 1);
  if (t >= PLANE.t0 && t < PLANE.t1) {
    const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(338, 142, u)), y = Math.round(lerp(49, 57, u));
    ctx.fillStyle = '#3a4270'; ctx.fillRect(x - 1, y, 3, 1);
    if (frac(t / 1.5) < 0.5) { ctx.fillStyle = '#ff5040'; ctx.fillRect(x + 1, y, 1, 1); }
    if (frac(t / 1.2) < 0.07) { ctx.fillStyle = '#ffffff'; ctx.fillRect(x - 1, y, 1, 1); halo(HL.strobe, x - 1, y, 1); }
  }
}

/* ================= Per-frame: the Eiffel Tower (beacon, gold, sparkle, red light) ================= */
function sparkleLevel(t) { let s = 0; for (const a of SPARKLE) s = Math.max(s, env(t, a, a + SPARK_DUR, 0.6)); return s; }
function drawTower(t) {
  const sx = TOWER.x, sy = TW.top + TW_BEACON, a = TAU * 40 * t / LOOP;
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = '#fff0cc';
  for (const off of [0, Math.PI]) {                                 // rotating beacon: two opposite beams
    const c = Math.cos(a + off), s = Math.sin(a + off), len = Math.round(140 * Math.abs(c)), dir = c < 0 ? -1 : 1;
    const I = 0.12 + 0.3 * Math.max(0, s);
    for (let i = 2; i < len; i += 2) {
      const hw = 0.5 + i * 0.022, y = sy - i * 0.06;
      ctx.globalAlpha = I * (1 - i / (len + 8)) * (0.8 + 0.2 * bayer(i >> 1, 0));
      ctx.fillRect(Math.round(sx + dir * i), Math.round(y - hw), 2, Math.max(1, Math.round(hw * 2)));
    }
  }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const sp = sparkleLevel(t);
  halo(HL.tower, sx, TOWER.base - 18, 0.9 + 0.35 * sp); halo(HL.towerCore, sx, TOWER.base - 10, 0.7);
  ctx.drawImage(TOWER_C.c, sx - TW.cx, TW.top);
  const flare = Math.pow(Math.max(0, Math.sin(a)), 14) + Math.pow(Math.max(0, Math.sin(a + Math.PI)), 14);
  halo(HL.beacon, sx, sy, 0.25 + 0.75 * flare);
  ctx.fillStyle = '#fff6dc'; ctx.fillRect(sx, sy, 1, 1);
  if (sp > 0) {                                                     // the sparkle: random white flashes all over
    const st = step(t, 3600), M = TOWER_C.mask;
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < 30; i++) {
      if (hash(i, st, 6) > 0.72) continue;
      const [mx, my] = M[Math.floor(hash(i, st, 5) * M.length)];
      const x = sx - TW.cx + mx, y = TW.top + my;
      ctx.globalAlpha = sp; ctx.fillRect(x, y, 1, 1);
      if (i < 9) halo(HL.spark, x, y, sp);
    }
    ctx.globalAlpha = 1;
  }
  const rl = 0.55 + 0.45 * Math.max(0, wave(t, 120));
  ctx.fillStyle = '#ff4a36'; ctx.fillRect(sx, TW.top, 1, 1); halo(HL.red, sx, TW.top, rl);
}

/* ================= Per-frame: rooftops life ================= */
function drawFarLights(t) {
  const st = step(t, 16);
  ctx.fillStyle = '#c89a5e';
  FAR.tiny.forEach(([x, y, r], i) => {
    if (hash(i, st, 17) > 0.72) return;
    ctx.globalAlpha = 0.45 + 0.4 * r; ctx.fillRect(x, y, 1, 1);
  });
  ctx.globalAlpha = 1;
  halo(HL.far, SPIRE.x, 118, 0.6);
}
function drawOutsideRain(t, far) {
  ctx.fillStyle = far ? 'rgba(150,166,210,0.2)' : 'rgba(170,186,222,0.3)';
  const len = far ? 2 : 4, sl = far ? 0.16 : 0.24, span = GL.y1 - GL.y0 + 16;
  for (const d of ORAIN) {
    if (d.far !== far) continue;
    const q = t * d.n / LOOP + d.ph, f = Math.floor(q), cyc = f % d.n, u = q - f;
    const y = GL.y0 - 8 + u * span, x = GL.x0 - 16 + hash(d.id, cyc, 1) * (GL.x1 - GL.x0 + 16) + (y - GL.y0) * sl;
    for (let k = 0; k < len; k++) {
      const yy = Math.round(y - k), xx = Math.round(x - k * sl);
      if (yy >= GL.y0 && yy < GL.y1 && xx >= GL.x0 && xx < GL.x1) ctx.fillRect(xx, yy, 1, 1);
    }
  }
}
function drawSmoke(t, list) {
  for (const s of list) for (let k = 0; k < 7; k++) {
    const u = frac(t * 24 / LOOP + k / 7 + s.ph);
    const x = s.x + u * 15 + wave(t, 17, s.ph + k * 0.13) * 1.2 * u, y = s.y - 1 - u * 19;
    const rr = Math.min(3, 1 + Math.floor(u * 3.2));
    ctx.globalAlpha = 0.42 * Math.min(1, u * 6) * (1 - u);
    ctx.drawImage(PUFF_S[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}
function wrect(w, x, y, ww, hh, col) {
  const x0 = Math.max(w.x, x), y0 = Math.max(w.y, y), x1 = Math.min(w.x + w.w, x + ww), y1 = Math.min(w.y + w.h, y + hh);
  if (x1 <= x0 || y1 <= y0) return;
  ctx.fillStyle = col; ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
}
function drawNeighbours(t) {
  for (const w of WINS) {
    let lit = 1, col = null;
    if (w.kind === 'water') {
      wrect(w, w.x, w.y, w.w, w.h, NB.warm); wrect(w, w.x, w.y, w.w, 3, NB.warmHi); wrect(w, w.x + 8, w.y, 2, w.h, NB.curtain);
      for (let k = 0; k < 3; k++) { const px = w.x + 1 + k * 3; wrect(w, px, w.y + 11, 2, 2, NB.pot); wrect(w, px, w.y + 9, 2, 2, NB.plant); wrect(w, px + (k & 1), w.y + 8, 1, 1, NB.plant); }
      const win = WATERING.find(([a, b]) => t >= a && t < b);
      if (win) {
        const [a, b] = win, tau = t - a, enter = smooth(0, 2.5, tau) * (1 - smooth(b - a - 2.5, b - a, tau));
        const px = Math.round(lerp(w.x + w.w + 4, w.x + 5 + Math.round(1.5 * (1 - Math.cos(TAU * tau / 16))), enter));
        const pour = enter > 0.97 && frac(tau / 4) < 0.55;
        wrect(w, px, w.y + 2, 2, 2, NB.sil); wrect(w, px - 1, w.y + 4, 4, 6, NB.sil); wrect(w, px - 1, w.y + 10, 1, 3, NB.sil); wrect(w, px + 2, w.y + 10, 1, 3, NB.sil);
        wrect(w, px - 3, w.y + 5, 2, 1, NB.sil); wrect(w, px - 5, w.y + 5, 2, 2, NB.sil2); wrect(w, px - 6, w.y + (pour ? 6 : 4), 1, 1, NB.sil2);
        if (pour) wrect(w, px - 6, w.y + 7 + Math.floor(frac(t * 3) * 2), 1, 1, '#a8c8e8');
      }
    } else if (w.kind === 'read') {
      if (t >= READ_OFF[0] && t < READ_OFF[1]) { lit = 0; wrect(w, w.x, w.y, w.w, w.h, NB.dark); wrect(w, w.x + 6, w.y + 1, 1, 3, '#1e2640'); }
      else {
        wrect(w, w.x, w.y, w.w, w.h, NB.orange); wrect(w, w.x, w.y, 4, w.h, '#cc7c3e');
        wrect(w, w.x, w.y + 2, 3, 2, '#f4c47c'); wrect(w, w.x + 1, w.y + 4, 1, 9, '#3a2014');
        wrect(w, w.x + 4, w.y + 7, 6, 6, '#3c1c12'); wrect(w, w.x + 8, w.y + 4, 2, 9, '#3c1c12');
        const nod = t % 40 > 33 ? 1 : 0;
        wrect(w, w.x + 5, w.y + 4 + nod, 2, 2, NB.sil2); wrect(w, w.x + 5, w.y + 6, 3, 4, NB.sil2);
        wrect(w, w.x + 3, w.y + 7, 2, 2, NB.page);
        if (PAGE_TURN.some(p => t >= p && t < p + 0.5)) wrect(w, w.x + (frac(t * 4) < 0.5 ? 3 : 4), w.y + 6, 1, 1, '#fff4d8');
      }
    } else if (w.kind === 'dance') {
      if (t < DANCE_ON[0] || t >= DANCE_ON[1]) { lit = 0; wrect(w, w.x, w.y, w.w, w.h, NB.dark); wrect(w, w.x + 2, w.y + 2, 1, 3, '#1e2640'); }
      else {
        wrect(w, w.x, w.y, w.w, w.h, NB.rose); wrect(w, w.x + 5, w.y, 5, 4, NB.roseHi);
        wrect(w, w.x, w.y, 1, w.h, '#7e3424'); wrect(w, w.x + 9, w.y, 1, w.h, '#7e3424');
        const sway = Math.round(1.3 * wave(t, 40)), bob = wave(t, 80) > 0.4 ? 1 : 0, swap = wave(t, 8) > 0;
        const cx = w.x + 4 + sway, tx = swap ? cx + 2 : cx, sx = swap ? cx : cx + 2;
        wrect(w, tx, w.y + 2, 2, 2, NB.sil); wrect(w, tx, w.y + 4, 2, 9, NB.sil);
        wrect(w, sx, w.y + 3 + bob, 2, 2, NB.sil); wrect(w, sx, w.y + 5 + bob, 2, 8, NB.sil);
      }
    } else {
      const h = hash(step(t, 1440), 7, 3);
      col = NB.tv[Math.floor(h * 6)];
      wrect(w, w.x, w.y, w.w, w.h, col); wrect(w, w.x, w.y + 9, w.w, 4, '#0c1020');
      wrect(w, w.x + 3, w.y + 6, 3, 3, '#0c1020'); wrect(w, w.x + 2, w.y + 8, 5, 2, '#0c1020');
      halo(HL.tv, w.x + 5, w.y + 6, 0.5 + 0.5 * h);
      continue;
    }
    if (lit) halo(HL.win, w.x + 5, w.y + 6, 0.9);
  }
}

/* ================= Per-frame: the billboard ================= */
/** intensity of the loose right-hand floodlamp (it stutters a few times a loop) */
function bbFlicker(t) {
  if (!BB_FLICK.some(([a, b]) => t >= a && t < b)) return 1;
  return hash(step(t, 1800), 41) < 0.55 ? 0.12 : 1;
}
function drawBillboard(t, pitch, rain) {
  const fl = bbFlicker(t);
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + 9, 0.8 + 0.2 * fl);              // lit rain haze around the sign
  ctx.drawImage(BB_FRAME, BBS.x, BBS.y);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(fl > 0.5 ? BB_FACE : BB_FACE_DIM, BB.x, BB.y);
    const s = step(t, 600);                                                     // bulb chase, 2.5 steps a second
    ctx.fillStyle = '#e0c088';
    BB_BULBS.forEach((x, k) => {
      if ((k + s) % 3 === 0) ctx.fillRect(BB.x + x, BB.y + 1, 1, 1);
      if ((BB_BULBS.length - 1 - k + s) % 3 === 0) ctx.fillRect(BB.x + x, BB.y + BB.h - 2, 1, 1);
    });
    ctx.fillStyle = '#f6ecd8';                                                  // steam curling off the cup
    for (const [wx, ph] of [[7, 0], [10, 0.45]]) for (let j = 0; j < 4; j++) {
      const x = wx + Math.round(Math.sin(j * 1.25 - TAU * (40 * t / LOOP + ph)) * 0.9);
      ctx.globalAlpha = 0.7 * (1 - j / 4.5) * (0.65 + 0.35 * wave(t, 24, ph + j * 0.1));
      ctx.fillRect(BB.x + x, BB.y + 7 - j, 1, 1);
    }
    ctx.globalAlpha = 1;
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {                                                 // lenses and their halos (both modes)
    const I = k === 2 ? fl : 1;
    ctx.fillStyle = I > 0.5 ? '#ecd6a4' : '#5e5046';
    ctx.fillRect(lx - 1, BB_LENS, 3, 1);
    halo(HL.bbLamp, lx, BB_LENS + 1, 0.85 * I);
  });
  if (!rain) return;
  ctx.fillStyle = '#ffe2b0';                                                    // rain glinting as it falls through the beams
  for (const d of BB_RAIN) {
    const q = t * d.n / LOOP + d.ph, f = Math.floor(q), cyc = f % d.n, u = q - f;
    const y = BB_LENS + 2 + u * 16, x = BB.x + 1 + hash(d.id, cyc, 1) * (BB.w - 6) + u * 4;
    let a = 0;
    BB_LAMPS.forEach((lx, k) => { a = Math.max(a, (k === 2 ? fl : 1) * (1 - Math.abs(x - lx) / 6)); });
    a *= 1 - u;
    if (a < 0.08) continue;
    ctx.globalAlpha = 0.4 * a;
    ctx.fillRect(Math.round(x), Math.round(y), 1, 2);
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: raindrops on the glass ================= */
const DB ={ n: 0, x: new Int16Array(700), y: new Int16Array(700), s: new Uint8Array(700), a: new Float32Array(700), m: new Uint8Array(700) };
const TR = { n: 0, x: new Int16Array(200), y0: new Float32Array(200), y1: new Float32Array(200), a: new Float32Array(200) };
function pushDrop(x, y, s, a, m = 0) { if (DB.n >= 700) return; const i = DB.n++; DB.x[i] = Math.round(x); DB.y[i] = Math.round(y); DB.s[i] = s; DB.a[i] = a; DB.m[i] = m; }
function pushTrail(x, y0, y1, a) { if (TR.n >= 200 || y1 - y0 < 1) return; const i = TR.n++; TR.x[i] = x; TR.y0[i] = y0; TR.y1[i] = y1; TR.a[i] = a; }
const RD = { dur: new Float32Array(6), pause: new Float32Array(6), wt: new Float32Array(6) };
function runner(R, t) {
  const q = t * R.n / LOOP + R.ph, f = Math.floor(q), cyc = f % R.n, u = q - f, T = LOOP / R.n, tau = u * T, id = R.id;
  const P = PANES[Math.floor(hash(id, cyc, 1) * 6)];
  const x0 = P.x0 + 3 + Math.floor(hash(id, cyc, 2) * (P.w - 8)), y0 = P.y0 + 2 + Math.floor(hash(id, cyc, 3) * P.h * 0.42);
  const yEnd = P.y1 - 3, K = 3 + Math.floor(hash(id, cyc, 4) * 3), tStart = T * (0.06 + 0.22 * hash(id, cyc, 5));
  let tot = 0, wsum = 0;
  for (let k = 0; k < K; k++) {
    RD.dur[k] = 0.3 + 0.7 * hash(id, cyc, 20 + k) + (k === K - 1 ? 0.25 : 0);
    RD.pause[k] = k === K - 1 ? 0 : 0.7 + 3.4 * hash(id, cyc, 30 + k);
    RD.wt[k] = (0.6 + hash(id, cyc, 10 + k)) * (1 + 0.9 * k); wsum += RD.wt[k];
    tot += RD.dur[k] + RD.pause[k];
  }
  const sc = Math.min(1, (T * 0.8 - tStart) / tot), tr0 = TR.n;
  let y = y0, x = x0, size = tau > tStart * 0.5 ? 2 : 1, tk = tStart, done = false, state = 0, dx = x0, dy0 = y0, tArr = 0;
  for (let k = 0; k < K; k++) {
    const dur = RD.dur[k] * sc, dy = (yEnd - y0) * RD.wt[k] / wsum, merge = k === 1 || k === 3;
    const jx = hash(id, cyc, 40 + k) < 0.35 ? (hash(id, cyc, 50 + k) < 0.5 ? -1 : 1) : 0;
    const nx = clamp(x + jx, P.x0 + 1, P.x1 - 5);
    if (!done) {
      if (tau < tk) { done = true; state = 0; dx = x; dy0 = y; }            // resting before this burst
      else {
        if (merge) size = Math.min(4, size + 1);
        const s = (tau - tk) / dur;
        if (s < 1) {                                                         // sliding
          const e = k === K - 1 ? s * s : s * s * (3 - 2 * s);
          pushTrail(nx, y, y + dy * e, 0.3);
          done = true; state = 1; dx = nx; dy0 = y + dy * e;
        } else {
          pushTrail(nx, y, y + dy, 0.3);
          if (k === K - 1) { done = true; state = 2; dx = nx; dy0 = yEnd - 1; tArr = tk + dur; }
        }
      }
    }
    if (merge && tau < tk) pushDrop(nx + (k === 3 ? 1 : 0), y + 3, k === 3 ? 2 : 1, 0.9);   // bead waiting to be absorbed
    y += dy; x = nx; tk += dur + RD.pause[k] * sc;
  }
  if (state === 1) pushDrop(dx, dy0, Math.max(3, size), 1, 1);
  else if (state === 2) {                                            // pooled on the rail, shrinking away
    const g = (tau - tArr) / (T - tArr);
    for (let i = tr0; i < TR.n; i++) TR.a[i] *= 1 - g;
    pushDrop(dx, dy0, Math.max(1, Math.round(size * (1 - g * 0.8))), 1 - g * g);
  } else pushDrop(dx, dy0, size, Math.min(1, tau / 0.4));
}
function drawGlass(t, rain) {                                       // rain: the beads, runners and rail beads (the mist stays)
  ctx.globalAlpha = 0.85 + 0.15 * wave(t, 3, 0.2);
  ctx.drawImage(FOG_C, OX, OY);
  ctx.globalAlpha = 1;
  DB.n = 0; TR.n = 0;
  if (rain) {
    ctx.drawImage(RAIL_C, OX, OY);
    for (const b of BEADS) {
      const q = t * b.n / LOOP + b.ph, f = Math.floor(q), cyc = f % b.n, u = q - f;
      if (u > 0.94) continue;
      const P = PANES[Math.floor(hash(b.id, cyc, 1) * 6)];
      const x = P.x0 + 1 + Math.floor(hash(b.id, cyc, 2) * (P.w - 2 - b.s)), y = P.y0 + 1 + Math.floor(hash(b.id, cyc, 3) * (P.h - 3 - b.s));
      pushDrop(x, y, u < 0.004 ? b.s + 1 : b.s, Math.min(1, (0.94 - u) / 0.05));
    }
    for (const R of RUNNERS) runner(R, t);
  }
  // trails: wet streaks, a little lighter than the glass
  ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#1e2a48';
  for (let i = 0; i < TR.n; i++) {
    const y0 = Math.round(TR.y0[i]), y1 = Math.round(TR.y1[i]);
    if (y1 <= y0) continue;
    ctx.globalAlpha = Math.min(1, TR.a[i] * 2.6);
    ctx.fillRect(TR.x[i] + 1, y0, 1, y1 - y0);
  }
  ctx.globalCompositeOperation = 'source-over'; ctx.fillStyle = '#c8d4ee';
  for (let i = 0; i < TR.n; i++) {
    const x = TR.x[i], y0 = Math.round(TR.y0[i]), y1 = Math.round(TR.y1[i]), sp = 4 + Math.floor(hash(x, y0, 3) * 4);
    ctx.globalAlpha = Math.min(1, TR.a[i] * 1.5);
    for (let yy = y0 + 2; yy < y1 - 3; yy += sp) if (hash(x, yy, 4) < 0.65) ctx.fillRect(x + (hash(x, yy, 5) < 0.3 ? 0 : 1), yy, 1, 1);
  }
  ctx.globalCompositeOperation = 'lighter';
  // drop shapes by size: 1 = speck, 2 = 1x2 bead, 3 = 2x3 drop, 4 = rounded 3x4 drop; m = sliding (longer)
  // bodies: additive (the drop gathers light), warm near the lamp
  for (let pass = 0; pass < 2; pass++) {
    ctx.fillStyle = pass ? '#36281a' : '#1e2638';
    for (let i = 0; i < DB.n; i++) {
      const s = DB.s[i];
      if (s < 2) continue;
      const x = DB.x[i], y = DB.y[i], m = DB.m[i];
      if ((Math.abs(x - LAMP.x) + Math.abs(y - LAMP.y) < 70 ? 1 : 0) !== pass) continue;
      ctx.globalAlpha = DB.a[i];
      if (s === 2) ctx.fillRect(x, y + 1, 1, 1 + m);
      else if (s === 3) ctx.fillRect(x, y, 2, 3 + m);
      else { ctx.fillRect(x + 1, y, 1, 1); ctx.fillRect(x, y + 1, 3, 2 + m); ctx.fillRect(x + 1, y + 3 + m, 1, 1); }
      if (m) ctx.fillRect(x + (s > 3 ? 1 : 0), y - 1, 1, 1);           // pointed top while sliding
    }
  }
  ctx.globalCompositeOperation = 'source-over';
  // dark refraction rim under each drop
  ctx.fillStyle = '#04070f';
  for (let i = 0; i < DB.n; i++) {
    const s = DB.s[i];
    if (s < 2) continue;
    const x = DB.x[i], y = DB.y[i], m = DB.m[i];
    ctx.globalAlpha = DB.a[i] * 0.55;
    if (s === 2) ctx.fillRect(x, y + 2 + m, 1, 1);
    else if (s === 3) { ctx.fillRect(x, y + 3 + m, 2, 1); ctx.fillRect(x + 2, y + 1, 1, 2 + m); }
    else { ctx.fillRect(x, y + 3 + m, 1, 1); ctx.fillRect(x + 2, y + 3 + m, 1, 1); ctx.fillRect(x + 1, y + 4 + m, 1, 1); ctx.fillRect(x + 3, y + 1, 1, 2 + m); }
  }
  // highlights: a glint at the top and a faint caustic low in the bigger drops
  for (let pass = 0; pass < 2; pass++) {
    ctx.fillStyle = pass ? '#ffe0a8' : '#d4dff6';
    for (let i = 0; i < DB.n; i++) {
      const x = DB.x[i], y = DB.y[i], s = DB.s[i], m = DB.m[i];
      if ((Math.abs(x - LAMP.x) + Math.abs(y - LAMP.y) < 70 ? 1 : 0) !== pass) continue;
      const a = DB.a[i];
      if (s === 1) { ctx.globalAlpha = a * 0.32; ctx.fillRect(x, y, 1, 1); continue; }
      ctx.globalAlpha = a * (s === 2 ? 0.6 : 0.85);
      if (s === 2) ctx.fillRect(x, y, 1, 1);
      else if (s === 3) { ctx.fillRect(x, y, 1, 1); ctx.globalAlpha = a * 0.28; ctx.fillRect(x + 1, y + 2 + m, 1, 1); }
      else { ctx.fillRect(x, y + 1, 1, 1); ctx.globalAlpha = a * 0.32; ctx.fillRect(x + 1, y + 2 + m, 1, 1); }
    }
  }
  ctx.globalAlpha = 1;
  // faint reflections of the room in the glass: the lamp and the string lights
  halo(HL.refl, 176, 152, 0.9);
  ctx.fillStyle = '#ffd08a';
  for (const b of BULBS) if (b.x > GL.x0 + 2 && b.x < GL.x1 - 2 && (b.x < 235 || b.x > 244)) { ctx.globalAlpha = 0.12; ctx.fillRect(b.x, GL.y0 + 3 + (b.y - BEAM.y1) * 0.5 | 0, 1, 1); }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: life in the room ================= */
function drawLights(t) {
  BULBS.forEach((b, i) => {
    let I = 0.78 + 0.22 * wave(t, b.k, b.ph);
    if (i === 19 && BULB_FLICK.some(([a, c]) => t >= a && t < c)) I = hash(step(t, 2400), 5) < 0.5 ? 0.15 : 0.9;
    ctx.fillStyle = BULB_COL[b.c]; ctx.globalAlpha = 0.55 + 0.45 * I;
    ctx.fillRect(b.x, b.y + 1, 2, 2);
    ctx.globalAlpha = 1; ctx.fillStyle = '#2a1c14'; ctx.fillRect(b.x, b.y, 2, 1);
    halo(HL.bulb, b.x + 1, b.y + 2, I);
  });
}
function drawTurntable(t) {
  const a = TAU * 133 * t / LOOP;                                   // 33.25 rpm, 133 turns per loop
  ctx.fillStyle = '#f2cfa8';
  ctx.fillRect(Math.round(TT.cx + Math.cos(a) * 3.3), Math.round(TT.cy + Math.sin(a) * 1.15), 1, 1);
  ctx.fillStyle = '#6a2a14';
  ctx.fillRect(Math.round(TT.cx + Math.cos(a + Math.PI) * 3.3), Math.round(TT.cy + Math.sin(a + Math.PI) * 1.15), 1, 1);
  ctx.fillStyle = '#5a5a6a';
  for (const [rr, o] of [[10.5, 1], [13, 3.4]]) ctx.fillRect(Math.round(TT.cx + Math.cos(a + o) * rr), Math.round(TT.cy + Math.sin(a + o) * rr * TT.ry / TT.rx), 1, 1);
  ctx.fillStyle = '#ff6a3a'; ctx.fillRect(66, 198, 1, 1); halo(HL.led, 66, 198, 0.7 + 0.3 * wave(t, 60));
}
function drawCat(t) {
  const d = t - STRETCH;
  let key = wave(t, 60, 0.1) > 0 ? 'inhale' : 'exhale';
  if (d >= 0 && d < 6) key = d < 0.8 || d > 5.1 ? 'half' : 'full';
  const pose = CAT_POSES[key], X = CAT.x, Y = CAT.y;
  ctx.drawImage(CAT_SPR[key], X, Y);
  drawEar(t, pose, X, Y);
  let lift = 0;                                                     // tail tip flick
  for (const f of TAIL_FL) { const e = t - f; if (e >= 0 && e < 1.4) lift = Math.round(2 * Math.abs(Math.sin(e / 1.4 * Math.PI * 2))); }
  if (lift && key !== 'full') {
    const tx = X + pose.tail[0];
    ctx.fillStyle = CATC.k; ctx.fillRect(tx - 1, Y + 16 - lift, 2, lift);
    ctx.fillStyle = CATC.rimW; ctx.fillRect(tx - 1, Y + 15 - lift, 1, 1);
  }
}
function drawSteam(t) {
  for (let k = 0; k < 12; k++) {
    const u = frac(t * 30 / LOOP + k / 12);
    const x = BOWL.x - 3 + (k % 3) * 2 + wave(t, 23, k * 0.17) * 2.2 * u + Math.sin(u * 6 + k) * 1.4 * u, y = BOWL.y - 2 - u * 30;
    const rr = u < 0.3 ? 1 : 2;
    ctx.globalAlpha = 0.55 * Math.min(1, u * 5) * Math.pow(1 - u, 1.2);
    ctx.drawImage(PUFF_W[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.fillStyle = '#fbf2e2';                                         // a thin curling wisp
  for (let j = 0; j < 16; j++) {
    const y = BOWL.y - 3 - j * 1.5, x = BOWL.x + Math.sin(j * 0.45 - TAU * 18 * t / LOOP) * (0.6 + j * 0.13);
    ctx.globalAlpha = 0.4 * (1 - j / 16) * (0.6 + 0.4 * wave(t, 20, j * 0.05));
    ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
  }
  ctx.globalAlpha = 1;
}
/** a moth drawn to the desk lamp: an erratic orbit round the bulb, wings flapping, or folded on the shade */
function drawMoth(t) {
  let f = 0;
  for (const [a, b] of MOTH_FLY) f = Math.max(f, env(t, a, b, 2.5));
  if (f === 0) {
    ctx.fillStyle = '#8c7658'; ctx.fillRect(MOTH.x - 1, MOTH.y, 3, 1);
    ctx.fillStyle = '#4a3624'; ctx.fillRect(MOTH.x, MOTH.y - 1, 1, 1);
    return;
  }
  const ox = 7 * Math.sin(TAU * 47 * t / LOOP) + 2.5 * Math.sin(TAU * 127 * t / LOOP + 1.3);
  const oy = 4 * Math.sin(TAU * 59 * t / LOOP + 0.7) + 2 * Math.sin(TAU * 151 * t / LOOP + 2.1);
  const x = Math.round(lerp(MOTH.x, MOTH.cx + ox, f)), y = Math.round(lerp(MOTH.y, MOTH.cy + oy, f)), up = frac(t * 12) < 0.5;
  ctx.fillStyle = '#3a2a1c'; ctx.fillRect(x, y, 1, 2);
  ctx.fillStyle = '#b89a72'; ctx.fillRect(x - 1, up ? y - 1 : y, 1, 1); ctx.fillRect(x + 1, up ? y - 1 : y, 1, 1);
}
function drawDust(t) {
  const col = ['#e8b878', '#ffd89a'];
  for (const m of MOTES) {
    const q = t * m.n / LOOP + m.ph, f = Math.floor(q), cyc = f % m.n, u = q - f;
    const bx = 124 + hash(m.id, cyc, 1) * 150, by = 128 + hash(m.id, cyc, 2) * 96;
    const x = bx + (u - 0.5) * (18 + 10 * m.layer) + wave(t, m.k1, m.p1) * (2 + m.layer * 2);
    const y = by + (u - 0.5) * (hash(m.id, cyc, 3) - 0.6) * 30 + wave(t, m.k2, m.p2) * (2 + m.layer * 1.5);
    const X = Math.round(x), Y = Math.round(y), L = lightAt(X, Y);
    const a = Math.min(1, u / 0.1, (1 - u) / 0.1) * (0.12 + 0.88 * L) * [0.45, 0.8, 0.9][m.layer];
    if (a < 0.03) continue;
    ctx.globalAlpha = a;
    if (m.layer === 2) ctx.drawImage(PUFF_D, X - 1, Y - 1);
    else { ctx.fillStyle = col[m.layer]; ctx.fillRect(X, Y, 1, 1); }
  }
  ctx.globalAlpha = 1;
}

/* ================= Frame compositor (back to front) ================= */
// billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's
// own when left out. Every weather depth falls outside, before the glass and the room (so the near call's grade tints
// only the view)
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawTower(t);
  ctx.drawImage(FAR.c, OX, OY);
  drawFarLights(t);
  if (wx.rain) drawOutsideRain(t, true);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  ctx.drawImage(MID.c, OX, OY);
  drawSmoke(t, SMOKE_FAR);
  if (billboard) drawBillboard(t, pitch, wx.rain);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawBalloon(t);
  ctx.drawImage(NEAR.c, OX, OY);
  drawNeighbours(t);
  drawPigeons(t);
  drawSmoke(t, SMOKE_NEAR);
  if (wx.rain) drawOutsideRain(t, false);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  drawGlass(t, wx.rain);
  ctx.drawImage(ROOM, 0, 0);
  drawMouse(t);
  drawLights(t);
  drawPlant(PLANT_L, t, 12, 0.1);
  drawPlant(PLANT_R, t, 14, 0.6);
  drawTurntable(t);
  drawCat(t);
  drawSteam(t);
  ctx.globalCompositeOperation = 'lighter'; ctx.drawImage(CONE_C, 0, 0); ctx.globalCompositeOperation = 'source-over';
  halo(HL.lamp, LAMP.x, LAMP.y, 0.95); halo(HL.lampCore, LAMP.x, LAMP.y, 0.9);
  drawMoth(t);
  drawDust(t);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'paris',
  name: 'Paris',
  tagline: 'Rain on the attic window',
  timeZone: 'Europe/Paris',
  accent: '#f4a261',
  focusX: TOWER.x,
  landmark: [TOWER.x - 7, TOWER.x + 8],                 // the Eiffel Tower
  landmarkTop: TOWER.base - TOWER.h,                    // the row of the tower's antenna tip, which landscape crops keep
  billboard: BB,
  perch: { dy: -1 },                                   // the What's new pigeon (site/js/ui/pigeon.js): the frame sits a row lower
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: TOWER.x - 9, y: TOWER.base - TOWER.h, w: 19, h: TOWER.h },   // the Eiffel Tower
    { kind: 'cat', x: CAT.x + 2, y: CAT.y, w: 44, h: 19 },                               // asleep on the sill
    { kind: 'birds', x: PIGEONS.x - 1, y: PIGEONS.y - 9, w: 18, h: 10 },                 // the pair on the chimney stack
    { kind: 'window', x: WINS[1].x, y: WINS[1].y, w: WINS[1].w, h: WINS[1].h },          // the reader's window across the street
    // things in the room that add a part to the music while they are on, and stop it when tapped again (site/js/audio/layers.js)
    { kind: 'shaker', name: 'Turntable', x: TT.cx - 26, y: TT.cy - 7, w: 62, h: 19 },      // the deck, its record and arm
    { kind: 'pad', name: 'Desk lamp', x: LAMP.x - 13, y: LAMP.y - 13, w: 19, h: 21 },      // its shade
    { kind: 'twinkle', name: 'Painting', x: EASEL.x0, y: EASEL.y0, w: EASEL.w + 1, h: EASEL.h },   // the canvas on the easel
  ],
  secrets: [                                           // hidden things a tap finds, each a passport sticker (site/js/ui/secrets.js)
    { id: 'mouse', name: 'a mouse on the bookshelf', x: MOUSE.x, y: MOUSE.y, w: 9, h: 7, art: [
      '............', '..sp...sp...', '.sppS.sppS..', '.sSSssssSs..', 'ssskssssssS.', 'psssssssssSS',
      '.sssssssssSS', '..wwwwwwsSS.', '..n..n..nNP.', '..........P.', '.........PP.', '............'] },
    { id: 'heart', name: 'a heart on the misted glass', x: HEART.x - 1, y: HEART.y - 1, w: 9, h: 10, art: [
      'NNNNNNNNNNNN', 'NKKKKKKKKKKN', 'NKKwwKKwwKKN', 'NKwKKwwKKwKN', 'NKwKKKKKKwKN', 'NKwKKKKKKwKN',
      'NKKwKKKKwKKN', 'NKKKwKKwKKKN', 'NKKKKwwKKKKN', 'NKKKKKwKKKKN', 'NKKKKKwKKKKN', 'NNNNNNNNNNNN'] },
    { id: 'balloon', name: 'a red balloon', x: BALLOON.x - 2, y: BALLOON.y - 1, w: 8, h: BALLOON.rise + 1, at: BALLOON_UP, art: [
      '............', '....rrrr....', '...rrrrrr...', '..rrwrrrrR..', '..rwwrrrrR..', '..rrrrrrrR..',
      '...rrrrRR...', '....rRRR....', '.....RR.....', '.....s......', '......s.....', '.....s......'] },
  ],
  ambience: { rain: 0.7, wind: 0.15, traffic: 0.1 },
  events: [
    ...SPARKLE.map(t => ({ t, type: 'chime', x: TOWER.x, gain: 0.6 })),
    ...BELL.map(t => ({ t, type: 'bell', x: SPIRE.x, gain: 0.55 })),
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
